// FILE: src/App.js
// Root component. Owns all session state and orchestrates the UI flow:
//   0. Admin overlay   (AdminScreen / QuestionEditor / BulkImport) — over any screen
//   1. Exam selection  (ExamSelector)
//   2. Mode selection  (ModeSelector)
//   3. Question loop   (QuestionCard + ExplanationPanel + SessionTimer)
//   4. Results page    (ResultsPage) — replaces old simple completion screens
//
// ── Skip / revisit model ──────────────────────────────────────────────────────
//
//   skippedIndices  Set<number>   Question indices skipped and not yet answered.
//
//   Forward pass: Q0 → Q1 → … → Q(n-1), marking skipped questions in the set.
//   Revisit pass: after the forward pass ends, cycles through the set in
//   ascending order.  Answering or dismissing (re-skipping) a question removes
//   it from the set.  The session completes when the set empties.
//
// ── Previous / question-state model ──────────────────────────────────────────
//
//   highWaterMark   number        The highest question index that has been
//                                 *processed* (submitted or skipped) via
//                                 handleNext / handleSkip.  Increases
//                                 monotonically; never changed by Previous.
//                                 Used to determine whether we are genuinely
//                                 in the revisit pass when the user navigates
//                                 back to a question that is in skippedIndices.
//
//   questionStates  object        Per-question snapshot:
//                                 { [index]: { selectedAnswers, submitted } }
//                                 Saved whenever the user leaves a question
//                                 (Next, Skip, or Previous).  Restored when
//                                 the user arrives at a question (Next, Skip
//                                 returning to the same index is not possible,
//                                 but Previous restores the prior question).
//
//   handlePrevious  DOES NOT touch highWaterMark or skippedIndices — only
//                   saves the current question's snapshot and restores the
//                   prior one.
//
// ── Finish Exam Now model ─────────────────────────────────────────────────────
//
//   showFinishConfirm  boolean    Controls the confirmation modal overlay.
//                                 Set true when the user clicks "Finish Exam
//                                 Now"; set false when they Cancel or confirm.
//
//   earlyFinish  boolean          Set true by handleFinishEarly().  Passed to
//                                 ResultsPage as completionReason='early'.
//
//   handleFinishEarly()           Saves the current question's snapshot (so
//                                 any in-progress selection is preserved in
//                                 questionStates), then sets currentIndex to
//                                 session.questions.length, which triggers the
//                                 ResultsPage on the next render.
//                                 highWaterMark and skippedIndices are NOT
//                                 altered — they reflect the true progress made
//                                 before the user chose to end early.
//
// ── Results page routing ──────────────────────────────────────────────────────
//
//   Three end-states all route to ResultsPage with a completionReason:
//     'expired'    — timer ran out (handleTimeExpired)
//     'early'      — user confirmed Finish Exam Now (handleFinishEarly)
//     'completed'  — natural completion (all questions resolved)
//
//   handleTimeExpired() now calls buildSavedStates() before transitioning so
//   the current question's in-progress state (submitted or not) is captured
//   in questionStates and correctly scored on the Results page.

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ExamSelector from './components/certification/ExamSelector';
import JourneyOnboarding from './components/journey/onboarding/JourneyOnboarding';
import { markLocalIntent as markJourneyIntent } from './services/journeyService';
import { captureEmailClick, stripEmailCampaignParams } from './services/email/emailClickService';
import ModeSelector from './components/session/ModeSelector';
import QuestionCard from './components/session/QuestionCard';
import ExplanationPanel from './components/session/ExplanationPanel';
import SessionTimer from './components/session/SessionTimer';
import SessionResultsView from './components/session/SessionResultsView';
import PaceCard, { PaceChip } from './components/pace/PaceCard';
import PaceCoachStrip from './components/pace/PaceCoachStrip';
import ResultsPage from './components/session/ResultsPage';
import AdminScreen from './components/admin/AdminScreen';
import EmailAnalyticsScreen from './components/admin/EmailAnalyticsScreen';
import JourneyActivationScreen from './components/admin/JourneyActivationScreen';
import LegalFooter from './components/legal/LegalFooter';
import AdminUsersScreen from './components/admin/AdminUsersScreen';
import RegisteredUsersScreen from './components/admin/RegisteredUsersScreen';
import AdminGate from './components/admin/AdminGate';
import AdminQuickActions from './components/admin/AdminQuickActions';
import SeoHealthScreen from './components/admin/SeoHealthScreen';
import AdminDashboard from './components/admin/AdminDashboard';
import QuestionEditor from './components/questions/admin/QuestionEditor';
import BulkImport from './components/questions/admin/BulkImport';
import ReportModal from './components/questions/ReportModal';
import FeedbackHubView from './components/feedback/FeedbackHubView';
import ReportedQuestionsView from './components/questions/admin/ReportedQuestionsView';
import DataToolsPanel from './components/admin/DataToolsPanel';
import QuestionPerformance from './components/performance/QuestionPerformance';
import TelemetryDashboard from './components/performance/admin/TelemetryDashboard';
import ReleaseNotesView from './components/admin/ReleaseNotesView';
import BadgeManager from './components/achievements/admin/BadgeManager';
import TestimonialsManager from './components/admin/TestimonialsManager';
import QuestionFactoryScreen from './components/QuestionFactory/QuestionFactoryScreen';
import QuestionNavigator from './components/session/QuestionNavigator';
import ExamBreak from './components/session/ExamBreak';
import SectionCheckpoint, { CheckpointChips, computeUnresolvedRange } from './components/session/SectionCheckpoint';
import PaceBreakPanel from './components/pace/PaceBreakPanel';
import PMPixelLogo from './components/branding/PMPixelLogo';
import QuestionPreviewModal from './components/questions/QuestionPreviewModal';
import TargetedTestConfig from './components/session/TargetedTestConfig';
// Phase E2 — learner-side pages reparented out of portals into App's view tree (§6.9.1).
import LearnerDashboard from './components/dashboard/LearnerDashboard';
import PerformanceHistory from './components/performance/PerformanceHistory';
import StudyPlanView from './components/studyplan/StudyPlanView';
import StudyPlanEditor from './components/studyplan/StudyPlanEditor';
import ReadinessPage from './components/readiness/ReadinessPage';
import MilestonesPage from './components/achievements/MilestonesPage';
import FoundingTesterLanding from './components/founding-testers/FoundingTesterLanding';
import FoundersWallPage from './components/founding-testers/FoundersWallPage';
import FoundingTesterAdmin from './components/founding-testers/FoundingTesterAdmin';
import CaseStudiesScreen from './components/case-studies/admin/CaseStudiesScreen';
import CaseStudyPracticeScreen from './components/case-studies/learner/CaseStudyPracticeScreen';
import { fetchExamCaseBlocks } from './services/examCaseBlocks';
import CaseExamPanel from './components/case-studies/exam/CaseExamContext';
import { getQuestionType as getQuestionTypeDef } from './questionTypes';
import { getExamConfig, getActiveCertIds, getExamBreaks, getPaceConfig } from './config/exams';
import { isFeatureEnabled, isMetadataPoolEnabled } from './config/featureFlags';
import { computePace, derivePaceProfile, computePaceAnalytics, isPaceEnabled } from './services/paceService';
import usePaceInterventions from './hooks/usePaceInterventions';
import { loadQuestionsFromSupabase, loadQuestionById, loadQuestionsByIds, loadExplanationsByIds, updateQuestionLifecycle, normalizeSupabaseQuestion, createQuestionWithHistory } from './services/questionService';
import { ensureCertLoaded, ensureCertPool, invalidateCertPool, invalidateCertBank } from './services/certBankLoader';
import { mergeImportIntoStore } from './services/importStoreMerge';
import { prepareSessionQuestions } from './services/sessionDraw';
import { createSessionStartCoordinator } from './services/sessionStartCoordinator';
import { createReport, persistReport, updateReportInSupabase } from './services/reportService';
import { createFeedbackFromReport } from './services/feedbackService';
import { persistExamSession, shouldPersistAbandonedSession } from './services/sessionService';
import { useLearnerAuth } from './contexts/LearnerAuthContext';
import { consumeAuthReturn } from './services/authReturn';
import { trackPageView, trackEvent, normalizeUserAgent } from './services/telemetryService';
import useMediaQuery, { MQ } from './hooks/useMediaQuery';
import './styles/theme.css';
import css from './App.module.css';

// The landing-page footer Admin button is a test/E2E-only affordance as of
// the menu-gated admin entry (see HeaderUserMenu). Rendered only when the
// build is a jest run or an E2E bypass build - never for real users.
const SHOW_TEST_ADMIN_BUTTON =
  process.env.REACT_APP_E2E_BYPASS_AUTH === 'true' ||
  process.env.NODE_ENV === 'test';

const MODE_LABELS = {
  full:         'Full Exam',
  practice:     'Practice Exam',
  targeted:     'Targeted Practice',
  targetedTest: 'Targeted Test',
};

// localStorage key for PMI-style exam-break persistence (Simulation only).
// Stores breaksTaken / currentBreak / breakStartTime / lockedQuestionThreshold
// so a restored session resumes its break countdown correctly with the exam
// clock still paused.
const EXAM_BREAK_STORAGE_KEY = 'pmPixel.examBreakState';

// ── Dev/test-only break override ──────────────────────────────────────────────
// `?breakAfter=3,6` forces Simulation breaks after those question numbers and
// `&breakMins=0.5` shortens the countdown — so the full break flow (offer
// screen, section lock, countdown, auto-resume) can be validated without
// answering 60 real questions. Hard-gated to non-production hosts (localhost,
// staging--*, deploy-preview-*): on prod domains the params are ignored
// entirely and breaks always come from exams.js config.
function isNonProdHost() {
  if (process.env.NODE_ENV !== 'production') return true;
  const host = window.location.hostname;
  return (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host.startsWith('staging--') ||
    host.startsWith('deploy-preview-')
  );
}

// A deep link that went through Google/Microsoft sign-in is restored BEFORE
// the first render reads the URL, so the learner lands on the session they
// asked for instead of the home screen.
try { consumeAuthReturn(); } catch (_) { /* never blocks boot */ }

// Learner views that can be addressed by URL. Kept next to viewFromUrl() so the
// allow-list has exactly one home.
const URL_ADDRESSABLE_VIEWS = new Set([
  'home', 'dashboard', 'readiness', 'plan', 'milestones', 'history', 'results',
  'founding-tester', 'founding-testers',
]);

/** The allow-listed `?view=` in the address bar, or null. */
function viewFromUrl() {
  try {
    const v = new URL(window.location.href).searchParams.get('view');
    return v && URL_ADDRESSABLE_VIEWS.has(v) ? v : null;
  } catch (_) {
    return null;
  }
}

const BREAK_OVERRIDE_STORAGE_KEY = 'pmPixel.breakOverride';

function getTestBreakOverride() {
  if (!isNonProdHost()) return null;
  const params = new URLSearchParams(window.location.search);
  let raw = params.get('breakAfter');
  let minsRaw = params.get('breakMins');
  // Sticky across tabs, OAuth redirects, and reloads: persist in localStorage
  // (per-origin, so it can never leak to prod domains, which also ignore the
  // params entirely). `?breakAfter=off` clears it.
  try {
    if (raw && raw.toLowerCase() === 'off') {
      localStorage.removeItem(BREAK_OVERRIDE_STORAGE_KEY);
      return null;
    }
    if (raw) {
      localStorage.setItem(
        BREAK_OVERRIDE_STORAGE_KEY,
        JSON.stringify({ breakAfter: raw, breakMins: minsRaw })
      );
    } else {
      const saved = JSON.parse(localStorage.getItem(BREAK_OVERRIDE_STORAGE_KEY) || 'null');
      if (saved) {
        raw = saved.breakAfter;
        minsRaw = saved.breakMins;
      }
    }
  } catch {
    /* storage unavailable — URL-only behaviour */
  }
  if (!raw || raw.toLowerCase() === 'off') return null;
  const mins = parseFloat(minsRaw);
  const durationMinutes = Number.isFinite(mins) && mins > 0 ? mins : 10;
  const thresholds = raw
    .split(',')
    .map((n) => parseInt(n.trim(), 10))
    .filter((n) => Number.isInteger(n) && n > 0)
    .sort((a, b) => a - b);
  if (thresholds.length === 0) return null;
  return thresholds.map((afterQuestion) => ({ afterQuestion, durationMinutes }));
}

// Arm the override at app boot, while the original query string is still
// present — sign-in redirects strip it from the URL before the first exam
// ever starts. The console line makes the armed state diagnosable.
try {
  const bootOverride = getTestBreakOverride();
  if (bootOverride) {
    // eslint-disable-next-line no-console
    console.info(
      '[PM Pixel] break test override ACTIVE — breaks after Q' +
        bootOverride.map((b) => b.afterQuestion).join(', Q') +
        ` (${bootOverride[0].durationMinutes} min). Clear with ?breakAfter=off`
    );
  }
} catch {
  /* never let test tooling interfere with app boot */
}

// The certification a brand-new learner practices first to establish a baseline.
// Mirrors BASELINE_EXAM_ID in LearnerDashboardEmpty (the CTA copy is PMP-specific).
const BASELINE_EXAM_ID = 'pmp';

// ── Navigation helper ─────────────────────────────────────────────────────────
//
// Pure function.  Determines the next question index after the user submits an
// answer (handleNext) or skips a question (handleSkip).
//
//   fromIndex     — the question index just acted on
//   total         — session.questions.length
//   newSkipped    — skipped Set AFTER applying the current action
//   wasRevisiting — true only when the forward pass is genuinely complete AND
//                   fromIndex was in skippedIndices before the action
//
// Returns the next index, or `total` to signal session completion.

function getNextQuestion(fromIndex, total, newSkipped, wasRevisiting) {
  if (!wasRevisiting) {
    // ── Forward pass: advance sequentially ──────────────────────────────────
    const next = fromIndex + 1;
    if (next >= total) {
      return newSkipped.size > 0 ? Math.min(...newSkipped) : total;
    }
    return next;
  }

  // ── Revisit pass: cycle through remaining skipped questions ─────────────
  const sortedSkipped = [...newSkipped].sort((a, b) => a - b);
  if (sortedSkipped.length === 0) return total;
  const nextAfter = sortedSkipped.find((i) => i > fromIndex);
  return nextAfter !== undefined ? nextAfter : sortedSkipped[0];
}

const styles = {
  app: {
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    minHeight: '100vh',
    background: '#f5f5f5',
    padding: '32px 16px',
  },
  header: {
    maxWidth: '1100px',
    margin: '0 auto 24px',
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto auto',
    alignItems: 'center',
    gap: '16px',
  },

  // ── Two-column exam layout (question + navigator) ─────────────────────────
  contentArea: {
    display: 'flex',
    gap: '20px',
    maxWidth: '1100px',
    margin: '0 auto',
    alignItems: 'flex-start',
  },
  questionColumn: {
    flex: 1,
    minWidth: 0,
  },
  navigatorColumn: {
    width: '310px',
    flexShrink: 0,
    position: 'sticky',
    top: '24px',
    maxHeight: 'calc(100vh - 80px)',
    overflowY: 'auto',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  // Groups "Finish Exam Now" and "← Change Mode" into one header cell so the
  // grid column count stays at four.
  headerActions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  examBadge: {
    padding: '4px 12px',
    background: '#333',
    color: '#fff',
    borderRadius: '20px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
    letterSpacing: '1px',
  },
  modeBadge: {
    padding: '4px 12px',
    background: '#f0f0f0',
    color: '#444',
    borderRadius: '20px',
    fontSize: '0.8rem',
    fontWeight: '600',
    border: '1px solid #ccc',
  },
  // Non-prod only: shows that the ?breakAfter test override is armed, so a
  // tester never has to guess whether breaks will fire early.
  breakOverrideChip: {
    padding: '4px 12px',
    background: '#fff3cd',
    color: '#7a5d00',
    borderRadius: '20px',
    fontSize: '0.75rem',
    fontWeight: '600',
    border: '1px solid #e0c36a',
    whiteSpace: 'nowrap',
  },
  progressCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
  },
  progress: {
    fontSize: '0.85rem',
    color: '#666',
    textAlign: 'center',
  },
  activeRefId: {
    fontFamily: "'Consolas', 'Monaco', monospace",
    fontSize: '0.72rem',
    color: '#999',
    background: '#f0f0f0',
    border: '1px solid #e0e0e0',
    borderRadius: '3px',
    padding: '1px 6px',
    letterSpacing: '0.4px',
  },
  resetButton: {
    padding: '4px 12px',
    background: 'transparent',
    border: '1px solid #999',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.8rem',
    color: '#555',
    whiteSpace: 'nowrap',
  },
  // "Finish Exam Now" — same shape as resetButton but red-tinted to signal
  // a destructive / ending action.
  finishButton: {
    padding: '4px 12px',
    background: 'transparent',
    border: '1px solid #c62828',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.8rem',
    color: '#c62828',
    whiteSpace: 'nowrap',
  },
  supabaseErrorBanner: {
    maxWidth: '1100px',
    margin: '0 auto 20px',
    padding: '12px 16px',
    background: '#ffebee',
    border: '1px solid #ef9a9a',
    borderRadius: '6px',
    fontSize: '0.85rem',
    color: '#c62828',
    lineHeight: '1.5',
  },
  revisitBanner: {
    // Sits inside questionColumn — no need for its own maxWidth or auto margin.
    marginBottom: '16px',
    padding: '8px 16px',
    background: '#fff3e0',
    border: '1px solid #ffb74d',
    borderRadius: '4px',
    fontSize: '0.85rem',
    color: '#e07b00',
    fontWeight: '600',
    textAlign: 'center',
  },
  // ── Finish Exam Now confirmation modal ────────────────────────────────────
  //
  // A fixed-position overlay that dims the page and centres the modal card.
  // Using position:fixed means it renders correctly regardless of scroll
  // position and doesn't affect the document layout.
  confirmOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  confirmModal: {
    background: '#fff',
    border: '2px solid #333',
    borderRadius: '8px',
    padding: '36px 32px',
    maxWidth: '400px',
    width: '90%',
    textAlign: 'center',
  },
  confirmHeading: {
    fontSize: '1.3rem',
    fontWeight: 'bold',
    color: '#c62828',
    marginTop: 0,
    marginBottom: '12px',
  },
  confirmBody: {
    fontSize: '0.95rem',
    color: '#555',
    lineHeight: '1.6',
    marginBottom: '28px',
  },
  confirmActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
  },
  confirmCancelButton: {
    padding: '10px 28px',
    background: 'transparent',
    border: '1px solid #999',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '1rem',
    color: '#555',
  },
  confirmEndButton: {
    padding: '10px 28px',
    background: '#c62828',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 'bold',
  },
};

export default function App() {
  // ── Admin overlay state ───────────────────────────────────────────────────
  //
  // showAdmin   boolean     — true while the admin overlay is mounted.  The
  //                           overlay renders in front of whatever the learner
  //                           was doing; all learner state is preserved.
  //
  // adminView   string|null — null            = Admin hub
  //                           'editor'        = Question Editor
  //                           'bulk-import'   = Bulk Import
  //
  // adminSource string|null — 'landing'   opened from the landing page
  //                           'session'   opened via shortcut from active Q
  //
  // adminExamId / adminQuestionId — set when opened from an active question so
  //   the editor can deep-link to that specific question immediately.
  const [showAdmin, setShowAdmin] = useState(false);
  const [adminView, setAdminView] = useState(null);
  const [adminSource, setAdminSource] = useState(null);
  const [adminExamId, setAdminExamId] = useState(null);
  const [adminQuestionId, setAdminQuestionId] = useState(null);
  // The case the editor was opened FROM (stage board "Edit" link) — lets the
  // editor's Back return to that exact case, not just the case list.
  const [adminCaseId, setAdminCaseId] = useState(null);
  // When true, the editor mounts with its "Unreviewed" filter chip already
  // toggled on. Used by Bulk Import's "Review imported questions" hand-off.
  const [adminFilterUnreviewed, setAdminFilterUnreviewed] = useState(false);
  // Array of sourceBatchId values the editor should scope its list to. Also
  // set by the Bulk Import hand-off so the reviewer sees just the questions
  // they just imported instead of the entire unreviewed backlog. Empty array
  // means "no batch filter".
  const [adminBatchIds, setAdminBatchIds] = useState([]);

  // Mission Control drill-down: preselect cert/view for Reported Questions.
  const [adminReportedCert, setAdminReportedCert] = useState('all');
  const [adminReportedView, setAdminReportedView] = useState(null);

  // ── Report store ──────────────────────────────────────────────────────────
  //
  // reports         object[]    — accumulates across sessions; never cleared by
  //                               reset handlers so SMEs can review after finishing.
  //
  // reportModalState object|null — null = modal closed
  //                                { question, examId, selectedAnswers }
  //                                  = modal open for this question context
  const [reports,          setReports]          = useState([]);
  const [reportModalState, setReportModalState] = useState(null);

  // ── Shared question stores ────────────────────────────────────────────────
  //
  // Lifted from QuestionEditor so that:
  //   • BulkImport can detect conflicts against whatever is currently in the store
  //   • BulkImport can append imported questions so they immediately appear in
  //     QuestionEditor and new exam sessions
  //   • Exam sessions started after an import or editor edit use the current store
  //
  // null = not yet loaded for this exam; [] = loaded but empty.
  const [questionStores, setQuestionStores] = useState(() =>
    Object.fromEntries(getActiveCertIds().map((id) => [id, null])));

  // Session-draw stage 3 (PR 2): lightweight metadata pools, kept STRICTLY
  // separate from questionStores. Slots hold poolOnly (content-free) question
  // metadata for certs routed through ensureCertPool; every full-content
  // consumer (editor, imports, preview, session patching) keeps reading
  // questionStores and must never see these rows.
  const [metadataPools, setMetadataPools] = useState(() =>
    Object.fromEntries(getActiveCertIds().map((id) => [id, null])));

  // ── Phase 3.6 — deep-link preview (?preview=<id>&exam=<examId>) ──────────
  const [previewQuestion,      setPreviewQuestion]      = useState(null);
  const [previewLoading,       setPreviewLoading]       = useState(false);
  const [previewLoadError,     setPreviewLoadError]     = useState(null);
  const [previewParamsActive,  setPreviewParamsActive]  = useState(() => {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    return params.has('preview');
  });

  // URL-addressable session setup: honor a deep link of the form
  // ?view=session-setup&cert=<id> (bookmarks, refresh, article CTAs, and the
  // browser Back button all land on the mode picker for that cert).
  // ?view=case-study&cert=<id> selects the cert the same way, and additionally
  // opens the Case Study Practice screen (see showCaseStudies below).
  const [examId, setExamId] = useState(() => {
    try {
      const url = new URL(window.location.href);
      const v = url.searchParams.get('view');
      if (v === 'session-setup' || v === 'case-study') {
        const cert = url.searchParams.get('cert');
        if (cert && getActiveCertIds().includes(cert)) return cert;
      }
    } catch (_) { /* URL API unavailable */ }
    return null;
  });
  const [session, setSession] = useState(null);

  // ── PR 2A — asynchronous session-start status (lazy cert-bank loading) ────
  // Minimal status contract surfaced to the start controls (Commit 3 wiring):
  //   status: 'idle' | 'loading' | 'error';  certId: the cert being started;
  //   error:  a concise message when a load fails (Retry replays retryDescriptor).
  const [sessionStart, setSessionStart] = useState({ status: 'idle', certId: null, error: null });
  // Serializable descriptor of the last failed start, so Retry replays the exact
  // original configuration (never a stored function closure).
  const [retryDescriptor, setRetryDescriptor] = useState(null);
  // PR 2B: lazily-loaded pool for the Targeted Test config screen when the live
  // store hasn't populated yet (store stays authoritative when present).
  const [targetedConfigPool, setTargetedConfigPool] = useState(null);

  // ── Phase E2 — top-level view enum (§6.9.1 / §6.11.3) ─────────────────────
  // Replaces the implicit state machine for learner-side surfaces. The exam
  // flow (home/session/results/admin) still uses examId/session/showAdmin
  // internally; this enum gates which top-level surface renders.
  //   home      — exam selection / mode picker / in-session / results (delegated
  //               to the existing logic below)
  //   dashboard — LearnerDashboard
  //   history   — PerformanceHistory (embedded)
  //   plan      — StudyPlanView (embedded)
  //   readiness — ReadinessPage
  //   milestones — MilestonesPage
  //   admin     — admin overlay (delegated to existing showAdmin logic; this
  //               enum value mirrors it for forward-compat)
  // Initial view honors a `?view=<name>` query param so bookmarks,
  // refresh, and shared links land on the right page. Allow-listed to
  // valid learner views; anything else falls back to 'home'.
  const [view, setView] = useState(() => viewFromUrl() || 'home');

  // ── Reopenable completed-session review (?view=results&session=<uuid>) ───
  // `session` is the canonical identity of ONE attempt — never "the latest
  // session". `from` records where the learner came from, for telemetry only.
  const [reviewSessionId, setReviewSessionId] = useState(() => {
    try { return new URL(window.location.href).searchParams.get('session') || null; }
    catch (_) { return null; }
  });
  const [reviewSource, setReviewSource] = useState(() => {
    try { return new URL(window.location.href).searchParams.get('from') || null; }
    catch (_) { return null; }
  });
  const reviewSessionRef = useRef(reviewSessionId);
  reviewSessionRef.current = reviewSessionId;

  // Which cert tab Performance History was last showing. Opening a session
  // review unmounts that component, so without this the learner comes back to
  // the first cert instead of the one they were reading.
  const [historyExamId, setHistoryExamId] = useState(null);

  const openSessionReview = React.useCallback((sessionId, from) => {
    if (!sessionId) return;
    setReviewSessionId(String(sessionId));
    setReviewSource(from || null);
    setView('results');
  }, []);

  // Plant a proper pmPixelView state on the browser's initial history entry
  // so popstate has something meaningful to read when the user backs out
  // of a deep-linked load. Runs once on mount; replaceState avoids creating
  // an extra history entry.
  //
  // While we're here, tidy the post-auth URL cosmetics: the OAuth / email
  // flows redirect back with a marker param (`?auth=callback` /
  // `?auth=confirmed`) that nothing in the app reads, and Supabase leaves a
  // bare `#` behind after consuming the tokens from the fragment. Both are
  // stripped so the address bar shows a clean origin. Guards:
  //   • `auth=reset` is left intact (reserved for the password-recovery flow).
  //   • The hash is only cleared when it is empty — a fragment that still
  //     carries data (e.g. `#access_token=…` mid-flow) is never touched.
  const initialReplaceRef = useRef(false);
  React.useEffect(() => {
    if (initialReplaceRef.current) return;
    initialReplaceRef.current = true;
    try {
      const url = new URL(window.location.href);
      const authMarker = url.searchParams.get('auth');
      if (authMarker === 'callback' || authMarker === 'confirmed') {
        url.searchParams.delete('auth');
      }
      // Lifecycle-email click attribution (activation intervention #1). Report
      // the click BEFORE the campaign params are stripped from the address bar,
      // then remove them so a bookmark or share cannot re-report it.
      captureEmailClick(url.search);
      stripEmailCampaignParams(url);
      if (url.hash === '' || url.hash === '#') {
        url.hash = '';
      }
      window.history.replaceState(
        { pmPixelView: view, ...(examIdRef.current ? { pmPixelExam: examIdRef.current } : {}) },
        '', url.toString()
      );
    } catch (_) { /* History API unavailable */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  // Telemetry: emit a page_view whenever the learner-facing view changes.
  // Additive only — does not affect navigation behaviour.
  React.useEffect(() => {
    trackPageView(view);
  }, [view]);

  // Telemetry: emit a page_view for the active admin tool (admin:<tool>).
  React.useEffect(() => {
    if (showAdmin && adminView) trackPageView(`admin:${adminView}`);
  }, [showAdmin, adminView]);
  // HeaderUserMenu dispatches `pm-pixel:navigate` events with a {view} detail
  // payload. We listen here so the menu remains a sibling of App today; a
  // future follow-up moves HeaderUserMenu into App's render tree directly.
  React.useEffect(() => {
    function onNav(e) {
      const v = e?.detail?.view;
      if (!v) return;
      if (v === 'admin') {
        // Admin entry point (HeaderUserMenu -> gated Admin item). The admin
        // overlay lives outside the view enum - open it directly instead of
        // routing. 'landing' source keeps handleAdminBack's default behaviour:
        // closing the overlay falls through to wherever the learner was.
        setAdminSource('landing');
        setShowAdmin(true);
        return;
      }
      // Learner destination while the admin overlay is open: close the
      // overlay first, or the view changes invisibly underneath it (the
      // overlay renders above all learner routes) and the click appears
      // to do nothing.
      if (adminNavRef.current.open) handleAdminBack();
      setView(v);
    }
    window.addEventListener('pm-pixel:navigate', onNav);
    return () => window.removeEventListener('pm-pixel:navigate', onNav);
  }, []);

  // -- Browser back-button support ----------------------------------------
  // Each user-driven view change pushes a history entry so the browser back
  // button walks the learner back through the views they actually visited,
  // instead of dropping them out of the SPA on first press. URL gains a
  // ?view=<name> query as a free side effect. popstateRef guards against
  // the pushState/popstate ping-pong loop. firstViewRef skips the redundant
  // initial entry on mount.
  const popstateRef = useRef(false);
  const firstViewRef = useRef(true);
  // Live session context for the once-registered popstate listener. Refreshed
  // every render by an effect further down (after the session state exists).
  const backNavRef = useRef({ active: false, ended: false, onBreak: false });
  // Live admin-overlay context for the once-registered popstate listener,
  // refreshed every render so browser Back can step through the admin stack.
  const adminNavRef = useRef({ open: false, hasSubView: false });
  adminNavRef.current = { open: showAdmin, hasSubView: !!adminView };
  // Current view for the once-registered popstate listener, so it can tell
  // whether a popped entry actually changes the view (see onPop below).
  const viewRef = useRef(view);
  viewRef.current = view;
  // Selected-cert mirror for the once-registered popstate listener and the
  // [view] URL-sync effect (session setup is state inside 'home', not a view).
  const examIdRef = useRef(examId);
  examIdRef.current = examId;
  // Which session a timer tick belongs to (see timerSecondsLeftRef).
  const sessionStartedAtRef = useRef(null);

  // Give the admin overlay its own history entry the moment it opens, so the
  // browser Back button is never disabled inside Admin (previously, opening
  // Admin straight from a fresh single-entry tab left nothing to go back to
  // and Back was greyed out). The `pmPixelAdmin` marker lets this effect skip
  // re-pushing when the marker is already the current entry (e.g. overlay
  // closed via its own Back button, then reopened).
  const prevShowAdminRef = useRef(false);
  React.useEffect(() => {
    if (showAdmin && !prevShowAdminRef.current) {
      try {
        const st = window.history.state;
        if (!(st && st.pmPixelAdmin)) {
          window.history.pushState(
            { pmPixelView: viewRef.current, pmPixelAdmin: true },
            '',
            window.location.href
          );
        }
      } catch (_) { /* History API unavailable */ }
    }
    prevShowAdminRef.current = showAdmin;
  }, [showAdmin]);

  React.useEffect(() => {
    if (firstViewRef.current) {
      firstViewRef.current = false;
      return;
    }
    if (popstateRef.current) {
      popstateRef.current = false;
      return;
    }
    try {
      const url = new URL(window.location.href);
      const nav = backNavRef.current;
      const onSetup = view === 'home' && examIdRef.current && !nav.active && !nav.ended;
      if (onSetup) {
        // Session setup is state inside 'home' — give it its real URL.
        url.searchParams.set('view', 'session-setup');
        url.searchParams.set('cert', examIdRef.current);
      } else if (view === 'home') {
        url.searchParams.delete('view');
        url.searchParams.delete('cert');
      } else if (view === 'results') {
        // The attempt id is part of the address: refresh, bookmark and Back
        // all resolve to the same attempt.
        url.searchParams.set('view', view);
        url.searchParams.delete('cert');
        if (reviewSessionRef.current) url.searchParams.set('session', reviewSessionRef.current);
      } else {
        url.searchParams.set('view', view);
        url.searchParams.delete('cert');
        url.searchParams.delete('session');
        url.searchParams.delete('from');
      }
      window.history.pushState(
        {
          pmPixelView: view,
          ...(onSetup ? { pmPixelExam: examIdRef.current } : {}),
          ...(view === 'results' && reviewSessionRef.current
            ? { pmPixelReviewSession: reviewSessionRef.current } : {}),
        },
        '', url.toString()
      );
    } catch (_) {
      /* History API unavailable -- silent fallback */
    }
  }, [view]);

  React.useEffect(() => {
    function onPop(e) {
      const nav = backNavRef.current;
      // Mid-exam: never tear the learner out of the session on a Back press —
      // before this guard, Edge dropped to a blank page because the exam had
      // no history entry of its own. Restore the entry the pop consumed, then
      // route the intent through the existing End Exam confirmation. During a
      // break the press is swallowed (the break overlay has its own controls).
      if (nav.active) {
        try {
          window.history.pushState(
            { pmPixelView: 'home', pmPixelSession: true },
            '',
            window.location.href
          );
        } catch (_) { /* History API unavailable */ }
        if (!nav.onBreak) nav.confirmLeave?.();
        return;
      }
      // Admin overlay: route browser Back through the admin stack instead of
      // letting the learner `view` change underneath it (which would surface
      // the Learner Dashboard behind the overlay). Consume the press, restore
      // the consumed history entry, and step back one admin level:
      //   sub-screen (e.g. Question Bank Dashboard) -> Admin hub
      //   Admin hub                                  -> close the overlay
      const admin = adminNavRef.current;
      if (admin.open) {
        if (admin.hasSubView) {
          // Sub-screen: consume the press — restore the entry the pop took
          // and step back one admin level.
          try {
            window.history.pushState(
              { pmPixelView: (e.state && e.state.pmPixelView) || 'home' },
              '',
              window.location.href
            );
          } catch (_) { /* History API unavailable */ }
          setAdminView(null);
          return;
        }
        // Admin hub: close the overlay and land on the entry the pop
        // delivered (no consume-and-restore — restoring here left a
        // duplicate entry that cost the user a dead Back press later).
        handleAdminBack();
        const av = (e.state && e.state.pmPixelView) || 'home';
        popstateRef.current = av !== viewRef.current;
        setView(av);
        return;
      }
      // Case Study Practice overlay (URL-addressable): Back closes the
      // overlay and falls through to the entry the pop delivered — the
      // session-setup handling below realigns examId with that entry.
      if (caseNavRef.current) setShowCaseStudies(false);
      // Results page: Back returns to the home screen inside the SPA.
      if (nav.ended) nav.resetToHome?.();
      // Prefer the entry's own state; fall back to what the address bar says
      // before assuming home. Some same-document navigations arrive with a
      // null state — notably Supabase clearing the token fragment after an
      // OAuth redirect (assigning location.hash fires popstate in Chromium).
      // Defaulting to 'home' there dropped a learner who signed in from a
      // session-review link onto the landing page, with the review URL still
      // in the address bar.
      const v = (e.state && e.state.pmPixelView) || viewFromUrl() || 'home';
      // URL-addressable session setup: align the selected cert with the
      // popped entry. Restores the mode picker when backing INTO a
      // session-setup entry; clears it when backing OUT to plain home.
      // Session-active/ended paths returned above and are not affected.
      const poppedExam = (e.state && e.state.pmPixelExam) || null;
      if (v === 'home') {
        if (poppedExam && getActiveCertIds().includes(poppedExam)) {
          selectedExamRef.current = poppedExam;
          setExamId(poppedExam);
        } else if (!poppedExam && examIdRef.current) {
          selectedExamRef.current = null;
          setExamId(null);
        }
      }
      // Arm the ping-pong guard only when the popped view differs from the
      // current one. When they match, React bails out of setView and the
      // [view] effect never runs — a `true` flag would go stale and silently
      // swallow the next user-driven navigation's pushState (the
      // intermittent "back button stopped working" symptom).
      // Reopenable review: the entry carries its own attempt id, so stepping
      // back to it shows that attempt rather than the most recent one.
      if (v === 'results') {
        const popped = (e.state && e.state.pmPixelReviewSession) || null;
        let fromUrl = null;
        try { fromUrl = new URL(window.location.href).searchParams.get('session'); } catch (_) { /* no URL API */ }
        setReviewSessionId(popped || fromUrl || null);
      }
      popstateRef.current = v !== viewRef.current;
      setView(v);
    }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // True after the user clicks the "Targeted Test" tile in ModeSelector but
  // before they confirm a configuration in TargetedTestConfig.  We need this
  // intermediate state because the session is not yet built — the config UI
  // owns filter+count selection and only then calls handleStartTargetedTest.
  const [pendingTargetedTest, setPendingTargetedTest] = useState(false);
  // Case Study Practice (PR 3): learner flow overlay off the mode selector.
  // URL-addressable: ?view=case-study&cert=<id> lands directly on the case
  // list, mirroring the ModeSelector tile gate (cert opt-in + global flag).
  // A valid cert with case studies gated off falls back to that cert's mode
  // picker (examId still honors the link above); an invalid cert falls back
  // to cert select. Signed-out visitors get the screen's sign-in prompt.
  const [showCaseStudies, setShowCaseStudies] = useState(() => {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get('view') !== 'case-study') return false;
      const cert = url.searchParams.get('cert');
      if (!cert || !getActiveCertIds().includes(cert)) return false;
      const cfg = getExamConfig(cert);
      return !!(cfg && cfg.caseStudies && cfg.caseStudies.enabled
        && isFeatureEnabled('caseStudiesEnabled'));
    } catch (_) { return false; }
  });
  // Live overlay context for the once-registered popstate listener (declared
  // here, after the state it mirrors, to avoid a TDZ read on first render).
  const caseNavRef = useRef(false);
  caseNavRef.current = showCaseStudies;
  // Case blocks for Full Sims (Phase 2, PR 5): prefetched per cert in
  // handleSelectMode so buildAndStartSession stays synchronous. null/absent
  // means "build a normal exam" — graceful degradation by design.
  const examCaseBlocksRef = useRef({});

  // Phase E2: the embedded Study Plan page (view === 'plan') opens the plan
  // editor as a modal so learners can set/regenerate their target exam date.
  // planRefreshKey is bumped on save to force StudyPlanView to reload the plan.
  const [planEditorOpen, setPlanEditorOpen] = useState(false);
  const [planRefreshKey, setPlanRefreshKey] = useState(0);
  // Which cert the embedded Study Plan page is focused on (track switcher).
  const [planActiveCert, setPlanActiveCert] = useState('pmp');

  // Publish current context for the global Feedback button (mounted as a sibling
  // in index.js). It reads window.__pmPixelFeedbackContext on open and stays
  // fresh via the 'pm-pixel:context' event, so the feedback modal can auto-fill
  // the page and certification without threading props through the whole tree.
  React.useEffect(() => {
    const PAGE_LABELS = {
      dashboard: 'Dashboard', readiness: 'Readiness', plan: 'Study Plan',
      milestones: 'Milestones', history: 'Performance History',
    };
    const CERT_LABELS = { pmp: 'PMP', cpmai: 'CPMAI', 'pmi-acp': 'PMI-ACP', 'pmi-pmocp': 'PMI-PMOCP' };
    let route = view;
    let pageName;
    if (showAdmin) {
      route = `admin:${adminView || 'hub'}`;
      pageName = 'Admin';
    } else if (view === 'home') {
      if (session) {
        route = 'session';
        pageName = session.modeName || MODE_LABELS[session.mode] || 'Exam Session';
      } else {
        pageName = 'Home';
      }
    } else {
      pageName = PAGE_LABELS[view] || 'Home';
    }
    const certId = session?.examId || null;  // cert only meaningful during an active exam session; overview screens show page name only
    const ctx = {
      route, pageName, certId,
      certLabel: certId ? (CERT_LABELS[certId] || certId) : null,
    };
    try {
      window.__pmPixelFeedbackContext = ctx;
      window.dispatchEvent(new CustomEvent('pm-pixel:context', { detail: ctx }));
    } catch (_) { /* CustomEvent unavailable — non-fatal */ }
  }, [view, session, showAdmin, adminView]);

  // Tracks whether the current session has already been persisted to Supabase.
  // Reset to false whenever a new session starts so each session is written once.
  const sessionPersistedRef = useRef(false);
  // PR 2A: current selected certification, read synchronously by the session-
  // start coordinator AFTER ensureCertLoaded() resolves to detect a stale start.
  const selectedExamRef = useRef(examId);
  // Stable coordinator instance (concurrency state must survive re-renders) plus
  // a deps ref so the coordinator always calls the LATEST closures (live state).
  const coordinatorRef = useRef(null);
  const sessionStartDepsRef = useRef(null);

  // Wall-clock ms at which the learner arrived at the current question.  Used
  // by buildSavedStates() to accumulate `timeSpentSeconds` per question so the
  // Phase 1 analytics (migration 011) can compute avg-time metrics.
  // Reset on every currentIndex change via the effect below.
  const questionArrivalTimeRef = useRef(Date.now());

  // ── Navigation state ────────────────────────────────────────────────────────
  const [currentIndex, setCurrentIndex] = useState(0);

  // Indices of questions skipped and not yet answered.
  const [skippedIndices, setSkippedIndices] = useState(new Set());
  // Phase 3.6+ — display-only union of "ever skipped + not yet answered".
  // Survives a re-skip in the revisit pass (which removes from skippedIndices)
  // so the navigator keeps showing the question as skipped until answered.
  // Cleared when a question is submitted, when a session resets, etc.
  const [everSkippedIndices, setEverSkippedIndices] = useState(new Set());

  // Highest question index that has been *processed* (answered or skipped).
  // Starts at -1 (no questions processed yet).  Increases monotonically via
  // handleNext / handleSkip; never decremented by handlePrevious.
  // Used to distinguish genuine revisit-pass from backward navigation to a
  // question that happens to be in skippedIndices.
  const [highWaterMark, setHighWaterMark] = useState(-1);

  // Per-question snapshots: { [index]: { selectedAnswers, submitted } }
  // Populated when leaving a question; restored when arriving at one.
  const [questionStates, setQuestionStates] = useState({});

  // ── Per-question UI state (for the currently displayed question) ────────────
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [showExplanation, setShowExplanation] = useState(false);

  // eliminatedChoices — array of choice ids the learner has crossed out for
  // the current question.  Persisted per-question in questionStates snapshots
  // alongside selectedAnswers and submitted so navigation restores them.
  const [eliminatedChoices, setEliminatedChoices] = useState([]);

  // ── Timer expiry ─────────────────────────────────────────────────────────────
  const [timeExpired, setTimeExpired] = useState(false);

  // ── PMI-style scheduled exam breaks (Simulation / Full Exam only) ───────────
  //
  // breakState       null, or the break currently on screen:
  //                  { status: 'offered'|'active', breakNumber, totalBreaks,
  //                    afterQuestion, durationMinutes, startedAt }
  //                  startedAt is the epoch ms the countdown began ('active').
  // breaksTaken      afterQuestion thresholds already passed (taken OR
  //                  skipped) — each break is offered exactly once.
  // lockedThreshold  question indices < this value are locked: viewable, but
  //                  answers / flags / eliminations can no longer change.
  //                  Mirrors PMI: passing a break point seals the section,
  //                  whether or not the learner takes the break.
  //
  // All thresholds and durations come from exams.js via getExamBreaks() —
  // nothing is hardcoded here. While breakState is non-null, SessionTimer is
  // paused, so break time never reduces exam time.
  const [breakState, setBreakState]           = useState(null);
  const [breaksTaken, setBreaksTaken]         = useState([]);
  const [lockedThreshold, setLockedThreshold] = useState(0);

  // Persist break state (spec: survives a refresh). The active countdown is
  // wall-clock based (startedAt), so time elapsed while the page was gone is
  // accounted for automatically on restore.
  useEffect(() => {
    if (!session || session.mode !== 'full') return;
    try {
      if (!breakState && breaksTaken.length === 0 && lockedThreshold === 0) {
        localStorage.removeItem(EXAM_BREAK_STORAGE_KEY);
        return;
      }
      localStorage.setItem(EXAM_BREAK_STORAGE_KEY, JSON.stringify({
        sessionStartedAt: session.startedAt,
        examId: session.examId,
        breaksTaken,
        currentBreak: breakState,
        breakStartTime: breakState?.startedAt ?? null,
        lockedQuestionThreshold: lockedThreshold,
      }));
    } catch {
      /* storage unavailable — break persistence is best-effort */
    }
  }, [session, breaksTaken, breakState, lockedThreshold]);

  // ── Finish Exam Now ──────────────────────────────────────────────────────────
  // showFinishConfirm — true while the "End Exam Early?" modal is open.
  // earlyFinish       — true after the user confirmed early finish; drives
  //                     alternate copy on the completion screen.
  const [showFinishConfirm, setShowFinishConfirm] = useState(false);
  // checkpointState — non-null while the pre-break "unresolved questions"
  // checkpoint is on screen: { resume: 'next'|'skip', afterQuestion,
  // unresolved }. Transient by design: the interrupted move was never
  // committed, so a refresh simply lands back on the threshold question.
  const [checkpointState, setCheckpointState] = useState(null);
  const [earlyFinish, setEarlyFinish] = useState(false);
  // Set once the finished session has been saved: its id (so the results
  // screen has a permanent address) and the exam-clock seconds it consumed.
  const [completedSessionId, setCompletedSessionId] = useState(null);
  const [completedTimeUsedSeconds, setCompletedTimeUsedSeconds] = useState(null);

  // ── Question navigator state ──────────────────────────────────────────────
  //
  // flaggedIndices  Set<number>  — questions the learner has flagged for review.
  //                               Independent of answers; survives Submit.
  //                               Reset when a new session starts.
  //
  // navVisible      boolean      — whether the navigator panel is expanded.
  //                               Defaults to true; persists within a session.
  const [flaggedIndices, setFlaggedIndices] = useState(new Set());
  const [navVisible, setNavVisible] = useState(true);

  // ── Intelligent Pace Coaching (live indicator) ────────────────────────────
  // The Pace Engine (services/paceService) is pure; App.js only feeds it
  // primitives and renders <PaceCard>. Elapsed time is LIFTED from SessionTimer
  // via onTick (so it tracks the displayed countdown exactly and stays correct
  // across paused break time) rather than derived from wall-clock. Updates are
  // throttled to the live-card cadence so App re-renders ~once per tick window,
  // not once per second. All thresholds/milestones come from exams.js per cert.
  const [paceSecondsLeft, setPaceSecondsLeft] = useState(null);
  const lastPaceAppliedRef = useRef(-Infinity);
  // Canonical "time used": the exam clock the learner actually consumed.
  // SessionTimer counts down from the ALLOWANCE and is paused during PMI-style
  // breaks, so (allowance - remaining) is time on the exam, excluding breaks.
  // Held in a ref (every tick, no re-render) so it is exact at completion —
  // duration_minutes is the allowance and must never be reported as time used.
  //
  // The reading is STAMPED with the session it came from instead of being
  // cleared when a session starts: the timer's mount tick (a child effect) runs
  // BEFORE this component's effects, so a reset here would wipe the first
  // reading and a session that ended within the first second would record no
  // time at all.
  const timerSecondsLeftRef = useRef(null);
  sessionStartedAtRef.current = session ? session.startedAt : null;

  // Mobile-only accordion: whether the pace detail is expanded. Persisted so it
  // survives re-renders/refresh within the exam (desktop ignores it — the detail
  // is always shown there). Default collapsed to keep the mobile header compact.
  const [paceExpanded, setPaceExpanded] = useState(() => {
    try { return localStorage.getItem('pm-pixel:pace-expanded') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('pm-pixel:pace-expanded', paceExpanded ? '1' : '0'); } catch { /* best-effort */ }
  }, [paceExpanded]);

  // Learner pace preferences (Phase 7). Default ON for full exams and practice
  // exams; targeted tests are OFF unless the learner opts in (decision D5).
  // Anonymous learners
  // (no profile) get the defaults. The kill-switch and opt-in live in the
  // header user menu.
  const { profile: learnerProfile, user: learnerUser } = useLearnerAuth();
  const pacePrefEnabled = learnerProfile ? learnerProfile.pace_coaching_enabled !== false : true;
  const paceTargetedOptIn = learnerProfile ? learnerProfile.pace_coaching_in_targeted === true : false;

  const paceConfig = useMemo(
    () => (session ? getPaceConfig(session.examId) : null),
    [session]
  );
  const paceEnabled = !!session && isPaceEnabled({
    paceConfig,
    mode: session ? session.mode : null,
    prefEnabled: pacePrefEnabled,
    targetedOptIn: paceTargetedOptIn,
  });
  const answeredCount = useMemo(
    () => Object.values(questionStates).filter((s) => s && s.submitted).length,
    [questionStates]
  );

  const handleTimerTick = useCallback((secondsLeft) => {
    timerSecondsLeftRef.current = { startedAt: sessionStartedAtRef.current, secondsLeft };
    const tick = (paceConfig && paceConfig.liveTickSeconds) || 8;
    // Apply on the cadence boundary, and always on the final (0) tick.
    if (secondsLeft <= 0 || Math.abs(secondsLeft - lastPaceAppliedRef.current) >= tick) {
      lastPaceAppliedRef.current = secondsLeft;
      setPaceSecondsLeft(secondsLeft);
    }
  }, [paceConfig]);

  // Reset the lifted clock whenever a new session starts (key-remount of the
  // timer re-emits the full duration on mount, repopulating it).
  useEffect(() => {
    lastPaceAppliedRef.current = -Infinity;
    setPaceSecondsLeft(null);
    setCompletedSessionId(null);
    setCompletedTimeUsedSeconds(null);
  }, [session?.startedAt]);

  const paceSnapshot = useMemo(() => {
    if (!paceEnabled || paceSecondsLeft == null || !session) return null;
    const durationSeconds = session.durationMinutes * 60;
    return computePace({
      totalQuestions: session.questions.length,
      durationSeconds,
      answeredCount,
      elapsedSeconds: durationSeconds - paceSecondsLeft,
      config: paceConfig,
    });
  }, [paceEnabled, paceSecondsLeft, session, answeredCount, paceConfig]);

  // Personalization (§5): advisory profile from running accuracy, flags, and
  // answer-changes. Computed for the break panel (and the column it persists
  // feeds the results summary later). Correctness is computed internally and
  // never shown as a live score.
  const paceProfile = useMemo(() => {
    if (!paceEnabled || !session || !paceSnapshot || !paceSnapshot.valid) {
      return { tags: [], dominant: null };
    }
    const qs = Array.isArray(session.questions) ? session.questions : [];
    let correct = 0;
    let changes = 0;
    Object.entries(questionStates).forEach(([idx, st]) => {
      if (!st || !st.submitted) return;
      changes += st.answerChanges || 0;
      const q = qs[idx];
      const ca = q && Array.isArray(q.correctAnswers) ? q.correctAnswers : null;
      const sa = Array.isArray(st.selectedAnswers) ? st.selectedAnswers : [];
      // Registry scoring keeps interactive case types (ordering/matching)
      // correct here too; classic types score identically to the old compare.
      const def = q ? getQuestionTypeDef(q.type) : null;
      if (def ? def.score(q, sa)
              : (ca && sa.length === ca.length && ca.every((id) => sa.includes(id)))) correct += 1;
    });
    return derivePaceProfile({
      snapshot: paceSnapshot,
      answeredCount,
      correctCount: correct,
      flaggedCount: flaggedIndices.size,
      answerChangeCount: changes,
      config: paceConfig,
    });
  }, [paceEnabled, session, paceSnapshot, questionStates, answeredCount, flaggedIndices, paceConfig]);

  // Per-question elapsed (for the time-sink nudge), derived from the same lifted
  // pace clock so it excludes paused break time. Reset synchronously when the
  // question changes so a fresh question never inherits the prior one's time.
  const paceElapsedSeconds = (session && paceSecondsLeft != null)
    ? Math.max(0, session.durationMinutes * 60 - paceSecondsLeft)
    : 0;
  const qStartRef = useRef({ key: null, elapsed: 0 });
  if (qStartRef.current.key !== currentIndex) {
    qStartRef.current = { key: currentIndex, elapsed: paceElapsedSeconds };
  }
  const currentQuestionSeconds = Math.max(0, paceElapsedSeconds - qStartRef.current.elapsed);

  // Non-blocking coaching messages (transitions, recovery, milestones,
  // time-sink). Disabled during breaks (the break panel will own that surface).
  const { message: paceMessage, dismiss: dismissPaceMessage } = usePaceInterventions({
    enabled: paceEnabled && breakState === null,
    snapshot: paceSnapshot,
    answeredCount,
    currentQuestionSeconds,
    currentQuestionKey: currentIndex,
    milestones: paceConfig ? paceConfig.milestones : null,
    config: paceConfig,
    examId: session ? session.examId : null,
    mode: session ? session.mode : null,
  });

  // ── Browser Back support for sessions ─────────────────────────────────────
  // Keeps the popstate listener's view of the world current. handleReset is a
  // hoisted function declaration, so referencing it here is safe.
  React.useEffect(() => {
    const total = session && Array.isArray(session.questions) ? session.questions.length : 0;
    const ended = !!session && (timeExpired || currentIndex >= total);
    backNavRef.current = {
      active: !!session && !ended,
      ended: !!session && ended,
      onBreak: breakState !== null,
      confirmLeave: () => setShowFinishConfirm(true),
      resetToHome: handleReset,
    };
  });

  // ── Responsive layout ─────────────────────────────────────────────────────
  const isMobile = useMediaQuery(MQ.mobile);
  const isTablet = useMediaQuery(MQ.tablet);
  const showDrawerNav = isTablet; // navigator as drawer on tablet + mobile

  // ── Keyboard shortcut: Ctrl+` opens Admin ────────────────────────────────
  //
  // Behaviour depends on the current screen:
  //   Landing page        → open Admin hub
  //   Active question     → open Question Editor focused on the current Q
  //   Results / mode sel. → ignored
  //   Admin already open  → ignored (prevents double-open)
  useEffect(() => {
    function handleKeyDown(e) {
      if (!e.ctrlKey || e.key !== '`') return;
      if (showAdmin) return; // already open — do nothing

      const activeQuestion =
        examId && session && !timeExpired && !earlyFinish &&
        currentIndex < session.questions.length
          ? session.questions[currentIndex]
          : null;

      if (activeQuestion) {
        // Open editor directly, pre-focused on the current question
        setAdminSource('session');
        setAdminExamId(examId);
        setAdminQuestionId(activeQuestion.id);
        setAdminView('editor');
        setShowAdmin(true);
      } else if (!examId) {
        // Landing page — open Admin hub
        setAdminSource('landing');
        setShowAdmin(true);
      }
      // Mode selector, results page → silently ignored
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [examId, session, showAdmin, timeExpired, earlyFinish, currentIndex]);

  // PR 2C: the mount-time Supabase prefetch has been REMOVED. The app shell no
  // longer loads any question bank at startup; each certification's bank is
  // loaded on demand (session start, dashboard, admin tools) via certBankLoader.
  // questionStores therefore starts all-null and is filled lazily; every reader
  // treats a null slot as "not loaded yet".


  // PR 2B — Targeted Test config pool: when the shared store hasn't populated
  // for the selected cert yet, lazily load just that cert's bank (no static
  // loadQuestions). The store remains authoritative when present.
  useEffect(() => {
    if (!pendingTargetedTest) return undefined;
    if (questionStores[examId] !== null) return undefined; // store authoritative
    let cancelled = false;
    (async () => {
      try {
        // Stage 3 (PR 2): the config screen only reads selection metadata
        // (filter options + match counts), so pool-enabled certs use the
        // already-loaded metadataPools slot when present, or load the
        // lightweight pool — never the full bank.
        const result = isMetadataPoolEnabled(examId)
          ? (metadataPools[examId] != null
              ? { questions: metadataPools[examId] }
              : await ensureCertPool(examId))
          : await ensureCertLoaded(examId);
        if (!cancelled) setTargetedConfigPool(result.questions);
      } catch (err) {
        if (!cancelled) {
          console.warn('[App] targeted-config bank load failed:', err);
          setTargetedConfigPool([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [pendingTargetedTest, examId, questionStores, metadataPools]);

  // ── Phase 3.6 — resolve ?preview=<id>&exam=<examId> at mount ────────────
  // Try the in-memory store first; if not yet loaded, fetch from Supabase
  // directly so deep links work on a cold browser session.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const pid    = params.get('preview');
    const pexam  = params.get('exam') || 'pmp';
    if (!pid) return;

    const fromStore = (questionStores[pexam] || []).find((q) => q.id === pid);
    if (fromStore) {
      setPreviewQuestion(fromStore);
      setPreviewLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setPreviewLoading(true);
      setPreviewLoadError(null);
      try {
        // Single-row PK lookup instead of fetching the entire exam bank just
        // to find one question by id. Identified via slow_query telemetry on
        // 2026-05-31; the full-bank fallback was contributing to the 22s max
        // duration. loadQuestionById returns the same normalized shape so the
        // rest of the preview path is unchanged.
        const found = await loadQuestionById(pid, pexam);
        if (cancelled) return;
        if (!found) {
          setPreviewLoadError(`Question "${pid}" not found in exam "${pexam}".`);
        } else {
          setPreviewQuestion(found);
        }
      } catch (err) {
        if (!cancelled) setPreviewLoadError(err?.message || 'Failed to load preview question.');
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [questionStores]); // eslint-disable-line react-hooks/exhaustive-deps

  function closePreviewDeepLink() {
    setPreviewQuestion(null);
    setPreviewLoadError(null);
    setPreviewParamsActive(false);
    if (typeof window !== 'undefined' && window.history && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.delete('preview');
      url.searchParams.delete('exam');
      window.history.replaceState({}, '', url.toString());
    }
  }

  // ── Session completion persistence ───────────────────────────────────────
  // Fires once when a session ends (time expired, finish early, or all questions
  // answered).  The ref guard ensures we only write once per session even if the
  // component re-renders while the results page is visible.
  useEffect(() => {
    if (!session) return;

    const ended = timeExpired || currentIndex >= session.questions.length;
    if (!ended) return;
    if (sessionPersistedRef.current) return; // already persisted this session

    sessionPersistedRef.current = true;
    const reason = timeExpired ? 'expired' : earlyFinish ? 'early' : 'completed';
    const allowanceSeconds = (session.durationMinutes || 0) * 60;
    const lastTick = timerSecondsLeftRef.current;
    const remaining = timeExpired
      ? 0
      : (lastTick && lastTick.startedAt === session.startedAt ? lastTick.secondsLeft : null);
    const timeUsedSeconds = allowanceSeconds > 0 && typeof remaining === 'number'
      ? Math.max(0, Math.min(allowanceSeconds, Math.round(allowanceSeconds - remaining)))
      : null;
    setCompletedTimeUsedSeconds(timeUsedSeconds);

    const startedAtAtPersist = session.startedAt;
    // Promise.resolve(...) keeps this safe if persistence is stubbed or
    // returns nothing — the results screen must never depend on the write.
    Promise.resolve(
      persistExamSession(session, questionStates, skippedIndices, reason, flaggedIndices, { timeUsedSeconds }),
    )
      .then((savedSessionId) => {
        // The results screen becomes a real, reopenable address, so a refresh
        // (or a later visit) shows this exact attempt instead of dropping the
        // learner on the home screen. Guests have no saved session and keep
        // the plain URL.
        if (!savedSessionId) return;
        if (startedAtAtPersist !== session.startedAt) return; // a new session started meanwhile
        setCompletedSessionId(savedSessionId);
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('view', 'results');
          url.searchParams.set('session', savedSessionId);
          url.searchParams.delete('cert');
          window.history.replaceState(
            { ...(window.history.state || {}), pmPixelReviewSession: savedSessionId },
            '', url.toString(),
          );
        } catch (_) { /* History API unavailable */ }
      })
      .catch(() => { /* persistence is fire-and-forget; never blocks results */ });

    // Pace telemetry (anonymized): one summary row per timed session.
    if (paceEnabled) {
      const a = computePaceAnalytics(session, questionStates, {
        flaggedCount: flaggedIndices.size,
        elapsedSeconds: paceElapsedSeconds,
      });
      trackEvent('pace_session_summary', 'usage', {
        feature: 'pace_coaching',
        exam_id: session.examId,
        mode: session.mode,
        status: a.finishedAhead ? 'ahead' : 'behind',
        pace_finish_margin_min: Math.round(a.completionMarginSeconds / 60),
        pace_avg_seconds: Math.round(a.avgSecondsPerQuestion),
      });
    }
  }, [timeExpired, currentIndex, session]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Per-question arrival timer ──────────────────────────────────────────
  // Resets questionArrivalTimeRef every time the learner lands on a new
  // question.  buildSavedStates() consumes this ref to compute the elapsed
  // seconds and accumulates them into questionStates[index].timeSpentSeconds.
  useEffect(() => {
    questionArrivalTimeRef.current = Date.now();
  }, [currentIndex, session]);

  // ── Admin overlay helpers ─────────────────────────────────────────────────

  // Called by AdminScreen's back button — returns to wherever the user came from.
  function handleAdminBack() {
    setShowAdmin(false);
    setAdminView(null);
    setAdminSource(null);
    setAdminExamId(null);
    setAdminQuestionId(null);
    setAdminFilterUnreviewed(false);
  }

  // Shared Quick Actions footer (AdminQuickActions) — one navigation entry
  // point for every admin tool, mounted beside every admin sub-view. Cases
  // mirror the AdminScreen onOpen* callbacks in renderAdminContent so both
  // entry paths land in identical state. 'hub' returns to Mission Control.
  function handleQuickAction(toolId) {
    switch (toolId) {
      case 'hub':                 setAdminView(null); break;
      case 'questionFactory':     setAdminView('question-factory'); break;
      case 'reportedQuestions':   setAdminReportedCert('all'); setAdminReportedView(null); setAdminView('reported-questions'); break;
      case 'editor':              setAdminExamId(null); setAdminQuestionId(null); setAdminFilterUnreviewed(false); setAdminBatchIds([]); setAdminView('editor'); break;
      case 'bulkImport':          setAdminView('bulk-import'); break;
      case 'dashboard':           setAdminExamId(null); setAdminView('dashboard'); break;
      case 'questionPerformance': setAdminView('question-performance'); break;
      case 'telemetry':           setAdminView('telemetry'); break;
      case 'emailAnalytics':      setAdminView('email-analytics'); break;
      case 'badgeManager':        setAdminView('badge-manager'); break;
      case 'adminUsers':          setAdminView('admin-users'); break;
      case 'registeredUsers':     setAdminView('registered-users'); break;
      case 'foundingTesters':     setAdminView('founding-testers-admin'); break;
      case 'testimonials':        setAdminView('testimonials'); break;
      case 'caseStudies':         setAdminView('case-studies'); break;
      case 'seoHealth':           setAdminView('seo-health'); break;
      case 'releaseNotes':        setAdminView('release-notes'); break;
      case 'feedbackHub':         setAdminView('feedback'); break;
      case 'dataTools':           setAdminView('data-tools'); break;
      default: break;
    }
  }

  // Called by QuestionEditor's back button.
  //   Session source           → close overlay entirely (restore active question screen).
  //   Reported-questions source → return to the Reported Questions view.
  //   Landing source (default)  → return to Admin hub.
  //
  // In all cases, if there is an active session, we patch session.questions from
  // the current store so that any edits made in the editor are immediately visible
  // in the running session (correct answer, explanation, etc.).  Without this, the
  // session snapshot retains the original question data even after a successful save.
  //
  // ID normalisation: CPMAI question ids are numbers; PMP ids are strings.
  // String(q.id) makes the Map key type-consistent so the lookup is always correct.
  function handleEditorBack() {
    // Patch the running session's questions from the store before navigating away.
    if (session) {
      const store = questionStores[session.examId];
      if (store !== null) {
        const storeById = new Map(store.map((q) => [String(q.id), q]));
        const patchedQuestions = session.questions.map(
          (q) => storeById.get(String(q.id)) ?? q
        );
        // Only call setSession when something actually changed to avoid a spurious re-render.
        const hasChanges = patchedQuestions.some((q, i) => q !== session.questions[i]);
        if (hasChanges) {
          setSession((prev) => ({ ...prev, questions: patchedQuestions }));
        }
      }
    }

    if (adminSource === 'session') {
      setShowAdmin(false);
      setAdminView(null);
      setAdminSource(null);
      setAdminExamId(null);
      setAdminQuestionId(null);
    } else if (adminSource === 'reported-questions') {
      setAdminView('reported-questions');
      setAdminSource(null);
      setAdminExamId(null);
      setAdminQuestionId(null);
    } else if (adminSource === 'dashboard') {
      setAdminView('dashboard');
      setAdminSource(null);
      setAdminExamId(null);
      setAdminQuestionId(null);
    } else if (adminSource === 'case-studies') {
      // Opened from a case's stage board — return to that case.
      setAdminView('case-studies');
      setAdminSource(null);
      setAdminExamId(null);
      setAdminQuestionId(null);
    } else if (adminSource === 'question-factory') {
      setAdminView('question-factory');
      setAdminSource(null);
      setAdminExamId(null);
      setAdminQuestionId(null);
    } else {
      setAdminView(null);
    }
    // "Unreviewed" filter and batch scope are one-shot hand-offs from Bulk
    // Import — always clear them when the editor closes so re-opening the
    // editor later starts with filters off.
    setAdminFilterUnreviewed(false);
    setAdminBatchIds([]);
  }

  // Called by ReportedQuestionsView when the SME clicks "Edit Question".
  // Deep-links into the editor at the specific question and records the source
  // so Back returns to the Reported Questions view.
  function handleEditReportedQuestion(editExamId, editQuestionId) {
    setAdminExamId(editExamId);
    setAdminQuestionId(editQuestionId);
    setAdminSource('reported-questions');
    setAdminView('editor');
  }

  // Called by AdminDashboard when the user clicks a Ref ID in a filter panel.
  // Deep-links into the editor and records the source so Back returns to dashboard.
  function handleEditDashboardQuestion(editExamId, editQuestionId) {
    setAdminExamId(editExamId);
    setAdminQuestionId(editQuestionId);
    setAdminSource('dashboard');
    setAdminView('editor');
  }

  // Called by the Question Factory PipelineItemDetailModal after an admin
  // promotes a draft via Approve & Publish. Deep-links into the editor at
  // the just-published question so the SME can polish it without searching
  // the bank manually. Source = 'question-factory' so Back returns to the
  // run detail.
  function handleOpenInEditorFromFactory({ examId, questionId } = {}) {
    if (!examId || !questionId) return;
    setAdminExamId(examId);
    setAdminQuestionId(questionId);
    setAdminSource('question-factory');
    setAdminView('editor');
  }

  // Called by the Question Factory after any successful publish (single
  // or bulk). Reloads the affected cert's question bank from Supabase and
  // merges the new rows into the in-memory store so admins see the
  // freshly-published questions in the Editor / Reported Questions / etc.
  // without a hard refresh.
  async function handleQuestionStoreInvalidated(examId) {
    if (!examId) return;
    try {
      const fresh = await loadQuestionsFromSupabase(examId);
      const nonRetired = Array.isArray(fresh) ? fresh.filter((q) => q.status !== 'retired') : null;
      if (!nonRetired) return;
      setQuestionStores((prev) => ({ ...prev, [examId]: nonRetired }));
    } catch (e) {
      // Non-fatal: the next hard refresh will pick up the new rows.
      // eslint-disable-next-line no-console
      console.warn('[question-factory] store refresh failed for', examId, e?.message || e);
    }
  }

  // Persists newly imported questions to Supabase, then merges them into the
  // shared in-memory store.  Returns { saved: number, errors: string[] } so
  // BulkImport can surface per-question failures in its Done screen.
  async function handleImport(importExamId, newQuestions) {
    const errors = [];

    for (const q of newQuestions) {
      try {
        await createQuestionWithHistory({
          id:              q.id,
          examId:          importExamId,
          referenceId:     q.referenceId,
          // Explicit type from the normalized row (multiple_select,
          // enhanced_matching, ordered_sequence, …). Without this the RPC
          // wrapper falls back to the multiSelect flag and stamps everything
          // multiple_choice — the deep-well-pilot-01 batch bug.
          type:            q.type ?? null,
          question:        q.question,
          choices:         q.choices,
          correctAnswers:  q.correctAnswers,
          explanation:     q.explanation  || '',
          category:        q.category     || '',
          source:          q.source       ?? null,
          confidence:      q.confidence   || 'medium',
          multiSelect:     q.multiSelect  || false,
          changeNote:      `Bulk import — ${q.sourceBatchId || importExamId}`,
          status:          q.status       || 'new',
          reviewStatus:    q.reviewStatus || 'unreviewed',
          sourceBatchId:   q.sourceBatchId   || null,
          sourceGenerator: q.sourceGenerator || null,
          performanceDomain: q.performanceDomain ?? null,
          deliveryContext:   q.deliveryContext   ?? null,
          // PMI-ACP canonical metadata (migrations 032 + 033). Forwarded
          // verbatim from the normalized import row; the importer already
          // applied validation + nested/flat back-compat normalization.
          ecoDomain:         q.ecoDomain         ?? null,
          ecoTask:           q.ecoTask           ?? null,
          practiceArea:      q.practiceArea      ?? null,
          knowledgeArea:     q.knowledgeArea     ?? null,
          artifact:          q.artifact          ?? null,
          cognitiveLevel:    q.cognitiveLevel    ?? null,
          difficulty:        q.difficulty        ?? null,
          scenarioBased:     q.scenarioBased === true,
          examWeight:        q.examWeight        ?? null,
          // Secondary dimension (PMOCP focus_area, migration 073).
          focusArea:         q.focusArea         ?? null,
        });
      } catch (err) {
        console.error(`[handleImport] Failed to save question ${q.id}:`, err);
        errors.push(`${q.id}: ${err.message}`);
      }
    }

    // Keep the in-memory store honest (services/importStoreMerge.js): a
    // loaded bank gains the new rows immediately; an unloaded (null) slot stays
    // null so the next consumer loads the real bank instead of a store holding
    // only these rows — which made the next practice session start empty.
    setQuestionStores((prev) => {
      const next = mergeImportIntoStore(prev[importExamId], newQuestions);
      return next === prev[importExamId] ? prev : { ...prev, [importExamId]: next };
    });
    // Bulk Import loaded (and cached) this bank for conflict detection before
    // saving; drop it so the editor and sessions refetch with the new rows.
    if (newQuestions.length - errors.length > 0) {
      invalidateCertBank(importExamId);
      invalidateCertPool(importExamId);
    }

    return { saved: newQuestions.length - errors.length, errors };
  }

  /**
   * Calls the update_question_lifecycle RPC and refreshes the local store.
   * Used by ReportedQuestionsView (Question Bank tab) and QuestionEditor.
   *
   * @param {string} questionId
   * @param {string} examId
   * @param {{ status?: string, reviewStatus?: string }} changes
   * @param {string} [changeNote]
   */
  async function handleUpdateQuestionLifecycle(questionId, examId, changes, changeNote = '') {
    try {
      const persisted = await updateQuestionLifecycle({
        id:           questionId,
        status:       changes.status       || null,
        reviewStatus: changes.reviewStatus || null,
        changeNote,
      });

      setQuestionStores((prev) => {
        const list = prev[examId];
        if (!list) return prev;

        const normalized = persisted
          ? normalizeSupabaseQuestion(persisted, examId)
          : null;

        // If retiring via lifecycle, remove from the store
        const targetStatus = changes.status || (normalized?.status);
        if (targetStatus === 'retired') {
          return { ...prev, [examId]: list.filter((q) => String(q.id) !== String(questionId)) };
        }

        const updated = list.map((q) => {
          if (String(q.id) !== String(questionId)) return q;
          return normalized
            ? { ...q, ...normalized }
            : { ...q, ...(changes.status ? { status: changes.status } : {}), ...(changes.reviewStatus ? { reviewStatus: changes.reviewStatus } : {}) };
        });
        return { ...prev, [examId]: updated };
      });
    } catch (err) {
      console.warn('[App] handleUpdateQuestionLifecycle failed:', err.message);
      throw err; // re-throw so the caller can show an error
    }
  }

  // ── Report handlers ───────────────────────────────────────────────────────

  // Opens the ReportModal for a specific question.
  //   question        — normalized question object (id, referenceId, question text)
  //   reportExamId    — 'pmp' | 'cpmai'
  //   reportSelected  — choice ids the learner had selected at time of reporting
  function handleOpenReport(question, reportExamId, reportSelected = []) {
    setReportModalState({
      question,
      examId: reportExamId,
      selectedAnswers: [...reportSelected],
    });
  }

  // Called by ReportModal when the learner submits the form.
  // Creates a report object and appends it to the store; the modal transitions
  // to its own confirmation state and stays open until the user clicks "Done".
  function handleSubmitReport(data) {
    const report = createReport(data);
    setReports((prev) => [...prev, report]);
    persistReport(report); // fire-and-forget write-through to Supabase
    // Unify with the Feedback Hub: mirror the question report into feedback_items
    // so the admin console is a single repository. Additive and fire-and-forget —
    // never blocks the existing reports flow.
    createFeedbackFromReport({
      questionId:     report.questionId,
      referenceId:    report.referenceId,
      certification:  report.examId,
      examDomain:     reportModalState?.question?.category || null,
      sessionId:      session?.id || null,
      comments:       report.comments,
      reportCategory: report.category,
      questionText:   report.questionSnapshot,
    });
    // Telemetry: engagement with the quality loop. No free-text reason is sent.
    trackEvent('report_question_submitted', 'usage', { exam_id: report.examId });
  }

  // Called by ReportModal when the user closes it (Cancel, ×, or Done).
  function handleCloseReportModal() {
    setReportModalState(null);
  }

  // Updates a report's status and/or SME notes in-place (immutably).
  // Accepts a partial patch object, e.g. { status: 'fixed' } or { notes: 'Corrected.' }
  // or both together { status: "won't fix", notes: 'Out of scope.' }.
  function handleUpdateReport(reportId, patch) {
    setReports((prev) =>
      prev.map((r) => (r.reportId === reportId ? { ...r, ...patch } : r))
    );
    updateReportInSupabase(reportId, patch); // fire-and-forget write-through to Supabase
  }

  // Permanently removes a resolved report from the list.
  // Only intended for "fixed" reports — "won't fix" reports stay in the list
  // for audit purposes until an explicit purge feature is added.
  function handleRemoveReport(reportId) {
    setReports((prev) => prev.filter((r) => r.reportId !== reportId));
  }

  // ── Snapshot helpers ───────────────────────────────────────────────────────

  // Returns the updated questionStates map after saving the current question.
  //
  // Also accumulates wall-clock time spent on the current question into
  // `timeSpentSeconds`, using questionArrivalTimeRef.  This is the minimum
  // hook needed to feed Phase 1 analytics without changing the rest of the
  // session flow — the ref is reset on every currentIndex change by the
  // effect above, so each arrival→leave pair produces exactly one delta.
  function buildSavedStates() {
    const now = Date.now();
    const arrival = questionArrivalTimeRef.current || now;
    const deltaSeconds = Math.max(0, Math.round((now - arrival) / 1000));
    const prior = questionStates[currentIndex];
    const priorSeconds = prior?.timeSpentSeconds || 0;

    // Answer-change tracking (Intelligent Pace Coaching §5): count when a
    // question that already had a selection comes back with a DIFFERENT one —
    // the "second-guessing" signal. Net change per visit (this runs on
    // navigation), compared order-independently.
    const priorSelected = prior?.selectedAnswers || [];
    const priorChanges = prior?.answerChanges || 0;
    const changed =
      priorSelected.length > 0 &&
      (priorSelected.length !== selectedAnswers.length ||
        !priorSelected.every((id) => selectedAnswers.includes(id)));

    return {
      ...questionStates,
      [currentIndex]: {
        selectedAnswers: [...selectedAnswers],
        submitted: showExplanation,
        eliminatedChoices: [...eliminatedChoices],
        timeSpentSeconds: priorSeconds + deltaSeconds,
        answerChanges: priorChanges + (changed ? 1 : 0),
      },
    };
  }

  // Returns { selectedAnswers, submitted, eliminatedChoices, timeSpentSeconds }
  // for a given index, falling back to empty / unsubmitted defaults when the
  // question has never been seen.
  function restoreState(states, index) {
    const s = states[index] || {};
    return {
      selectedAnswers: s.selectedAnswers ?? [],
      submitted: s.submitted ?? false,
      eliminatedChoices: s.eliminatedChoices ?? [],
      timeSpentSeconds: s.timeSpentSeconds ?? 0,
    };
  }

  // Toggles a choice id in/out of the current question's elimination list.
  function handleToggleElimination(choiceId) {
    if (currentIndex < lockedThreshold) return; // locked section — read-only
    setEliminatedChoices((prev) =>
      prev.includes(choiceId)
        ? prev.filter((id) => id !== choiceId)
        : [...prev, choiceId]
    );
  }

  // ── Handlers ───────────────────────────────────────────────────────────────

  function handleSelectExam(id) {
    selectedExamRef.current = id;   // keep the coordinator's current-cert read in sync
    setExamId(id);
    // Session setup gets its own history entry + URL, so browser Back returns
    // to cert select and the mode picker survives refresh / deep links.
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('view', 'session-setup');
      url.searchParams.set('cert', id);
      window.history.pushState({ pmPixelView: 'home', pmPixelExam: id }, '', url.toString());
    } catch (_) { /* History API unavailable */ }
    trackEvent('cert_selected', 'usage', { cert: id, exam_id: id });
  }

  // Starts a PMP practice session directly. Used by the learner-journey /
  // dashboard / history "start" CTAs, which previously just navigated to the
  // home screen — dead-ending the "set a baseline" flow. Completing a
  // qualifying PMP session is what auto-sets the learner's baseline
  // (see sessionService Phase C baseline auto-set).
  function handleStartPractice() {
    selectedExamRef.current = BASELINE_EXAM_ID;
    setExamId(BASELINE_EXAM_ID);   // keep exam state in sync for back-navigation
    setView('home');               // leave any dashboard/history/plan view so the session can render
    handleSelectMode('practice', BASELINE_EXAM_ID);
  }

  // Start a specific cert's baseline session from the dashboard / readiness
  // track switcher. The "+ <cert>" buttons surface a "Set a <cert> baseline"
  // dialog first (LearnerDashboardEmpty); confirming it calls this with the
  // chosen cert. Mirrors handleStartPractice but cert-aware: completing a
  // qualifying session is what auto-sets the learner's baseline for that cert.
  function handleStartCert(certId) {
    selectedExamRef.current = certId;
    setExamId(certId);          // keep exam state in sync for back-navigation
    setView('home');            // leave the dashboard/readiness view so the session renders
    handleSelectMode('practice', certId);
  }

  // ── Journey Activation onboarding (dark: journeyOnboardingEnabled) ──────
  // Both handlers REUSE existing pipelines — no parallel session flows.

  // 5-question micro-practice = the targeted-test pipeline with an explicit
  // count and no filters (same shortcut ResultsPage's targeted quiz takes:
  // the config screen is skipped, everything downstream is shared).
  // mode_selected fires so the activation funnel and intent suppression see
  // it exactly like any other session start.
  function handleStartJourneyMicro(certId) {
    selectedExamRef.current = certId;
    setExamId(certId);
    setView('home');
    trackEvent('mode_selected', 'usage', { mode: 'targetedTest', exam_id: certId });
    markJourneyIntent();
    coordinatorRef.current.begin({
      certId,
      kind: 'targetedTest',
      mode: 'targetedTest',
      filters: {},
      count: 5,
    });
  }

  // 'Testing my readiness' → the existing practice / full-sim modes.
  function handleStartJourneyMode(certId, mode) {
    selectedExamRef.current = certId;
    setExamId(certId);
    setView('home');
    handleSelectMode(mode, certId);
  }

  // Gives a freshly started session its own history entry, so the browser
  // Back button has somewhere to go that is still inside the SPA. Popping
  // back to the pre-exam entry mid-session is intercepted in onPop above.
  function pushSessionHistoryEntry() {
    try {
      window.history.pushState(
        { pmPixelView: 'home', pmPixelSession: true },
        '',
        window.location.href
      );
    } catch (_) { /* History API unavailable */ }
  }

  // ── PR 2A — asynchronous session-start coordinator ──────────────────────
  // One owned path places the lazily-loaded bank into the question store. We
  // store result.questions directly (no extra transformed copy).
  function applyLoadedBank(certId, questions) {
    // Owned store path: fill the slot with the freshly loaded bank ONLY when it
    // is still empty — never clobber editor edits, bulk imports, or a prior load
    // already held in the store (mirrors the mount prefetch's non-clobber rule).
    setQuestionStores((prev) => (prev[certId] != null ? prev : { ...prev, [certId]: questions }));
  }

  // Owned metadataPools path (stage 3, PR 2): same non-clobber rule, separate
  // store. Receives ONLY poolOnly (content-free) metadata from ensureCertPool;
  // full-content banks never land here, and these rows never reach
  // questionStores or a rendered session (hydration replaces them first).
  function applyLoadedPool(certId, questions) {
    setMetadataPools((prev) => (prev[certId] != null ? prev : { ...prev, [certId]: questions }));
  }

  // Async draw + hydration step (stage 3, PR 2). The selection engine and the
  // store/pool routing live in services/sessionDraw (framework-agnostic, DI'd);
  // this closure feeds it the LIVE app state. The coordinator awaits it and
  // re-checks the supersede/stale guards afterwards, so content is always
  // complete before buildSessionFromPrepared runs — a session never renders a
  // metadata-only question.
  async function prepareQuestionsForSession(descriptor, result) {
    const { certId } = descriptor;
    const prepared = await prepareSessionQuestions(descriptor, result, {
      quarantinedIds: new Set(
        reports.filter((r) => r.status === 'new').map((r) => r.questionId)
      ),
      store: questionStores[certId],
      caseBlocks: examCaseBlocksRef.current[certId],
      caseExamsEnabled: isFeatureEnabled('caseStudiesInExamsEnabled'),
      applyLoadedBank,
      applyLoadedPool,
      loadQuestionsByIds,
      invalidateCertPool,
      // Stage 3 PR 3: hydrate without explanations; they load on demand at
      // Practice submit (single) and session end (batch).
      deferExplanations: isFeatureEnabled('deferSessionExplanations'),
    });
    // Activation reliability (2026-08-12): an empty prepared pool for any
    // non-targetedTest route is a retryable failure, never a silent
    // zero-question session. (Targeted Test keeps its decline path — the
    // config screen owns empty-filter feedback.) A throw here follows the
    // coordinator's prepareQuestions contract: error status + Retry +
    // session_start_failed telemetry.
    if (descriptor.kind !== 'targetedTest'
        && (!prepared || !Array.isArray(prepared.questions) || prepared.questions.length === 0)) {
      const err = new Error('No questions are available for this session right now. Please try again.');
      err.code = 'EMPTY_SESSION_POOL';
      throw err;
    }
    return prepared;
  }

  // ── Stage 3 PR 3 — deferred-explanation loading ───────────────────────────
  //
  // Pool-hydrated sessions may arrive with explanationDeferred rows (the
  // hydration fetch excluded the column). Two loaders patch the running
  // session in place — every consumer (ExplanationPanel, results/review,
  // report modal) keeps reading question.explanation unchanged:
  //   • per question at Practice/Targeted-Test submit (showExplanation), and
  //   • one batch at session end, so results/review have complete data.
  // Failures keep the deferred marker (and free the in-flight slot) so a
  // later render retries; nothing blocks the learner from advancing.
  const explanationFetchRef = useRef(new Set()); // ids with an in-flight fetch

  function patchSessionExplanations(rows) {
    if (!Array.isArray(rows) || rows.length === 0) return;
    const byId = new Map(rows.map((r) => [String(r.id), r.explanation ?? '']));
    setSession((prev) => {
      if (!prev) return prev;
      let changed = false;
      const questions = prev.questions.map((q) => {
        const text = byId.get(String(q.id));
        if (text === undefined || q.explanationDeferred !== true) return q;
        changed = true;
        return { ...q, explanation: text, explanationDeferred: false };
      });
      return changed ? { ...prev, questions } : prev;
    });
  }

  // Per-question: fetch the explanation the moment it becomes visible.
  useEffect(() => {
    if (!showExplanation || !session) return;
    const q = session.questions[currentIndex];
    if (!q || q.explanationDeferred !== true) return;
    const id = String(q.id);
    if (explanationFetchRef.current.has(id)) return;
    explanationFetchRef.current.add(id);
    (async () => {
      const rows = await loadExplanationsByIds(session.examId, [id]);
      explanationFetchRef.current.delete(id);
      if (rows) patchSessionExplanations(rows);
    })();
  }, [showExplanation, currentIndex, session]); // eslint-disable-line react-hooks/exhaustive-deps

  // Batch: at session end, fetch every still-deferred explanation at once.
  useEffect(() => {
    if (!session) return;
    const ended = timeExpired || currentIndex >= session.questions.length;
    if (!ended) return;
    const missing = session.questions
      .filter((q) => q.explanationDeferred === true)
      .map((q) => String(q.id))
      .filter((id) => !explanationFetchRef.current.has(id));
    if (missing.length === 0) return;
    missing.forEach((id) => explanationFetchRef.current.add(id));
    (async () => {
      const rows = await loadExplanationsByIds(session.examId, missing);
      missing.forEach((id) => explanationFetchRef.current.delete(id));
      if (rows) patchSessionExplanations(rows);
    })();
  }, [session, timeExpired, currentIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Synchronous "back half" of a session start: builds + starts the session
  // from the PREPARED draw ({ questions, caseAssembly } — already hydrated).
  // Shared by all three entry routes; mode-specific labels/durations are
  // derived from the serializable descriptor + live config. Returns true if a
  // session started, false if it declined (e.g. an empty targeted-test pool).
  function buildAndStartSession(descriptor, prepared) {
    const { certId, kind, mode, filters, phaseLabels } = descriptor;
    const config = getExamConfig(certId);
    const { questions, caseAssembly } = prepared;

    // Defense-in-depth: NEVER build a zero-question session in any mode.
    // prepareQuestionsForSession already throws for non-targetedTest routes;
    // this guard covers targetedTest (config screen blocks it, but guard
    // anyway) and any future entry route that skips the prepare-level check.
    if (questions.length === 0) {
      return false;
    }

    let durationMinutes;
    let modeName;
    if (kind === 'targetedTest') {
      const spq = config?.targetedTest?.secondsPerQuestion ?? 90;
      durationMinutes = Math.max(1, Math.ceil((questions.length * spq) / 60));
      const filterValueCount = Object.values(filters || {})
        .reduce((sum, vs) => sum + (Array.isArray(vs) ? vs.length : 0), 0);
      modeName = filterValueCount > 0
        ? `Targeted Test: ${filterValueCount} focus area${filterValueCount === 1 ? '' : 's'}`
        : MODE_LABELS.targetedTest;
    } else if (kind === 'targeted') {
      durationMinutes = config?.targetedQuiz?.durationMinutes ?? 20;
      modeName = `Targeted Practice: ${phaseLabels}`;
    } else { // selectMode (practice | full)
      durationMinutes = mode === 'full'
        ? config.fullExam.durationMinutes
        : config.practiceExam.durationMinutes;
      modeName = MODE_LABELS[mode];
    }

    sessionPersistedRef.current = false; // reset so the new session gets persisted once
    const startedAt = Date.now();
    setSession({
      examId: certId,
      displayName: config.displayName,
      mode,
      modeName,
      totalQuestions: questions.length,
      durationMinutes,
      questions,
      startedAt,
      // Case-block metadata (Phase 2) — absent on standard sessions.
      ...(caseAssembly ? { caseBlocks: caseAssembly.caseBlocks, breaks: caseAssembly.breaks } : {}),
    });
    // Stage 3 (PR 2): persist the drawn ID sequence alongside session init.
    // Refresh/resume (a future PR) must rebuild the session by fetching THESE
    // ids — never by performing another draw. Best-effort: storage failures
    // never block a session start.
    try {
      window.localStorage.setItem('pmPixelSessionDraw', JSON.stringify({
        examId: certId,
        mode,
        modeName,
        startedAt,
        durationMinutes,
        questionIds: questions.map((q) => String(q.id)),
        ...(caseAssembly ? {
          caseBlockIds: Object.keys(caseAssembly.caseBlocks),
          breaks: caseAssembly.breaks,
        } : {}),
      }));
    } catch (_) { /* storage unavailable — draw persistence is best-effort */ }
    pushSessionHistoryEntry();

    if (kind === 'targetedTest') setPendingTargetedTest(false);

    setCurrentIndex(0);
    setSkippedIndices(new Set());
    setEverSkippedIndices(new Set());
    setHighWaterMark(-1);
    setQuestionStates({});
    setSelectedAnswers([]);
    setEliminatedChoices([]);
    setShowExplanation(false);
    setTimeExpired(false);
    setShowFinishConfirm(false);
    setEarlyFinish(false);
    setFlaggedIndices(new Set());
    resetBreakState();
    setNavVisible(true);
    return true;
  }

  // Keep a deps ref updated every render so the stable coordinator instance
  // always invokes the LATEST closures (live reports/config/setters).
  // ── Activation observability (dormant-user analysis, Phase 1) ──────────────
  // Tracks the mode-selection → first-answer transition. Stages:
  //   session_start_requested → first_question_rendered (or session_start_empty
  //   / session_start_failed) → first_question_answered.
  // Bank-load failures/timings stay on the existing failed_load / slow_query
  // events — these events cover the learner-visible outcome layer above them.
  const activationRef = useRef({ requestedAt: null, renderedFor: null, answeredFor: null, loadInfo: null });

  function handleSessionLifecycle(stage, descriptor, info = {}) {
    const base = { exam_id: descriptor.certId, mode: descriptor.mode, kind: descriptor.kind };
    if (stage === 'requested') {
      activationRef.current.requestedAt = Date.now();
      // Device class inline (same normalizer as app_open) so funnel queries
      // don't need an app_session join to split desktop vs mobile.
      const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
      trackEvent('session_start_requested', 'usage', { ...base, ...normalizeUserAgent(ua) });
    } else if (stage === 'failed') {
      trackEvent('session_start_failed', 'technical', {
        ...base,
        duration_ms: info.duration_ms,
        message_short: info.message,
      });
    }
    else if (stage === 'built') {
      // Not an event — the render effect below is the truthful "learner saw a
      // question" signal. Stash which loader satisfied the start so the
      // rendered event can report pool vs bank vs bundled.
      activationRef.current.loadInfo = {
        load_source: info.load_source,
        load_status: info.load_status,
      };
    }
    // 'declined' is the guarded empty pool — config UI prevents it for
    // targeted tests; other modes throw at prepare and surface 'failed'.
  }

  // first_question_rendered / session_start_empty — fires once per built
  // session (keyed by startedAt; question-content patches re-set `session`
  // without changing startedAt, and must not re-fire).
  useEffect(() => {
    if (!session) return;
    const act = activationRef.current;
    if (act.renderedFor === session.startedAt) return;
    // Only observe sessions whose start we requested in THIS app visit —
    // resumed/restored sessions (refresh) are not new activation attempts.
    if (act.requestedAt == null) return;
    act.renderedFor = session.startedAt;
    act.answeredFor = null;
    const duration_ms = Date.now() - act.requestedAt;
    act.requestedAt = null;
    const meta = { exam_id: session.examId, mode: session.mode, duration_ms };
    if (session.totalQuestions === 0) {
      // A zero-question session is a silently broken start (empty bank/pool
      // after a load failure fell back to an empty bundled bank) — the exact
      // failure behind the 2026-08-10 pmi-pmocp dormant-user sessions.
      trackEvent('session_start_empty', 'technical', meta);
    } else {
      trackEvent('first_question_rendered', 'usage', {
        ...meta,
        question_count: session.totalQuestions,
        ...(act.loadInfo || {}),
      });
    }
  }, [session]);

  // first_question_answered — first committed selection of the session, any
  // mode (practice submits reveal explanations; full-exam answers are
  // selection + navigation, so selection is the shared earliest signal).
  useEffect(() => {
    if (!session || selectedAnswers.length === 0) return;
    const act = activationRef.current;
    if (act.answeredFor === session.startedAt) return;
    if (act.renderedFor !== session.startedAt) return; // resumed session — not a new activation
    act.answeredFor = session.startedAt;
    trackEvent('first_question_answered', 'learning', {
      exam_id: session.examId,
      mode: session.mode,
    });
  }, [selectedAnswers, session]);

  sessionStartDepsRef.current = {
    // Flag-aware loader (stage 3, PR 2): certs in REACT_APP_METADATA_POOL_CERTS
    // load the lightweight metadata pool; a populated questionStores slot
    // (editor edits / imports / admin visits) keeps the full-content path so
    // behaviour there is unchanged. All other certs use the legacy bank load.
    ensureCertLoaded: (id) =>
      (isMetadataPoolEnabled(id) && questionStores[id] == null
        ? ensureCertPool(id)
        : ensureCertLoaded(id)),
    getSelectedCertId: () => selectedExamRef.current,
    applyLoadedBank,
    prepareQuestions: prepareQuestionsForSession,
    buildSession: buildAndStartSession,
    setStatus: setSessionStart,
    setRetryDescriptor,
    onLifecycleEvent: handleSessionLifecycle,
  };
  if (!coordinatorRef.current) {
    coordinatorRef.current = createSessionStartCoordinator({
      ensureCertLoaded: (id) => sessionStartDepsRef.current.ensureCertLoaded(id),
      getSelectedCertId: () => sessionStartDepsRef.current.getSelectedCertId(),
      applyLoadedBank: (id, q) => sessionStartDepsRef.current.applyLoadedBank(id, q),
      prepareQuestions: (d, r) => sessionStartDepsRef.current.prepareQuestions(d, r),
      buildSession: (d, q) => sessionStartDepsRef.current.buildSession(d, q),
      setStatus: (st) => sessionStartDepsRef.current.setStatus(st),
      setRetryDescriptor: (d) => sessionStartDepsRef.current.setRetryDescriptor(d),
      onLifecycleEvent: (st, d, i) => sessionStartDepsRef.current.onLifecycleEvent(st, d, i),
    });
  }

  // Replays the last failed start with its exact original configuration.
  function handleRetrySessionStart() {
    if (!retryDescriptor) return;
    selectedExamRef.current = retryDescriptor.certId;
    coordinatorRef.current.begin(retryDescriptor);
  }

  // Minimal status contract handed to the start controls (no promises/refs/
  // request-ids cross this boundary — see SessionStartStatus).
  const sessionStartStatus = {
    status: sessionStart.status,
    certId: sessionStart.certId,
    error: sessionStart.error,
    canRetry: !!retryDescriptor,
  };

  async function handleSelectMode(mode, examIdArg) {
    // Allow an explicit exam id (e.g. from handleStartPractice) so callers
    // don't have to wait for the async setExamId state update to land.
    const activeExamId = examIdArg || examId;

    trackEvent('mode_selected', 'usage', { mode, exam_id: activeExamId });

    // Journey Activation: a mode selection is observed intent — permanently
    // suppresses the journey onboarding surface (device-local immediately;
    // reconciled into the profile the next time the surface mounts).
    markJourneyIntent();

    // Targeted Test takes a detour through TargetedTestConfig — the user
    // configures filters + count there and we build the session via
    // handleStartTargetedTest() once they confirm. No bank load here.
    if (mode === 'targetedTest') {
      trackEvent('targeted_test_started', 'usage', { exam_id: activeExamId });
      setPendingTargetedTest(true);
      return;
    }

    // Case Study Practice (PR 3): its own flow — list → intro → runner →
    // review. No bank load; cases come from the case RPCs.
    if (mode === 'caseStudy') {
      // URL-addressable (share/refresh-safe): push a ?view=case-study entry
      // so the address bar is copyable and browser Back returns to the mode
      // picker via the popstate overlay guard.
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('view', 'case-study');
        url.searchParams.set('cert', activeExamId);
        window.history.pushState({ pmPixelView: 'home', pmPixelExam: activeExamId }, '', url.toString());
      } catch (_) { /* History API unavailable */ }
      setShowCaseStudies(true);
      return;
    }

    // Case Studies inside Full Sims (Phase 2): prefetch published case blocks
    // BEFORE the coordinator builds, so assembly stays synchronous. Any
    // failure degrades to a standard exam — never blocks the session.
    if (mode === 'full'
        && isFeatureEnabled('caseStudiesInExamsEnabled')
        && getExamConfig(activeExamId)?.fullExam?.caseSection?.count > 0) {
      try {
        examCaseBlocksRef.current[activeExamId] = await fetchExamCaseBlocks(activeExamId);
      } catch (err) {
        console.warn('[caseBlocks] prefetch failed — building a standard exam:', err.message);
        examCaseBlocksRef.current[activeExamId] = null;
      }
    }

    // Lazy path: await the selected cert's bank, then build with an explicit
    // pool. The coordinator owns concurrency, stale-cert, and retry handling.
    selectedExamRef.current = activeExamId;
    coordinatorRef.current.begin({ certId: activeExamId, kind: 'selectMode', mode });
  }

  /**
   * Builds and starts a user-configured Targeted Test session.  Called from
   * TargetedTestConfig once the user has selected filters and count.
   *
   * Behaves like Practice Exam from here on (Question Table, explanations,
   * reveal-correctness in navigator) — the only difference vs. handleSelectMode
   * is the question pool is filtered to the chosen metadata values.
   *
   * @param {object}   payload
   * @param {Object<string,string[]>} payload.filters
   * @param {number}   payload.count
   */
  function handleStartTargetedTest({ filters, count }) {
    // Lazy path: await the selected cert's bank, then build with an explicit
    // pool. Mode name, duration, and the empty-pool guard live in
    // buildAndStartSession (shared with the other entry routes).
    selectedExamRef.current = examId;
    coordinatorRef.current.begin({
      certId: examId,
      kind: 'targetedTest',
      mode: 'targetedTest',
      filters,
      count,
    });
  }

  /**
   * Launches a targeted quiz session focused on the specified weak phases.
   * Called by ResultsPage after the user confirms in TargetedQuizModal.
   *
   * @param {PhaseMetric[]} weakPhases  1–2 phase metric objects from getWeakPhases()
   */
  function handleStartTargetedQuiz(weakPhases) {
    // Lazy path: await the selected cert's bank, then build with an explicit
    // pool. The descriptor stays serializable — phase keys + a readable label
    // fragment ("Phase II & Phase IV") — so Retry can replay it exactly.
    const phases      = weakPhases.map((p) => p.key);              // category strings
    const phaseLabels = weakPhases.map((p) => p.label).join(' & ');
    selectedExamRef.current = examId;
    coordinatorRef.current.begin({
      certId: examId,
      kind: 'targeted',
      mode: 'targeted',
      phases,
      phaseLabels,
    });
  }


  function handleTimeExpired() {
    // Save the current question's in-progress state so the Results page can
    // score it correctly (e.g. the user had submitted an answer and was reading
    // the explanation when time ran out — that answer should count).
    setQuestionStates(buildSavedStates());
    setTimeExpired(true);
    setShowExplanation(false);
    setShowFinishConfirm(false); // dismiss confirm dialog if it was open
  }

  function handleSubmit() {
    if (timeExpired) return;
    if (currentIndex < lockedThreshold) return; // locked section — read-only
    setShowExplanation(true);
  }

  // Called by ExplanationPanel's "Next Question →" button.
  //
  // Saves the current question's snapshot, removes it from skippedIndices
  // (in case this was a revisit answer), advances the high-water mark, and
  // navigates via getNextQuestion.
  // ── PMI-style exam-break helpers (Simulation / Full Exam only) ─────────────

  // Clears all break state and the persisted copy. Called from every
  // new-session / reset path so break state never leaks across sessions.
  function resetBreakState() {
    setBreakState(null);
    setBreaksTaken([]);
    setLockedThreshold(0);
    try {
      localStorage.removeItem(EXAM_BREAK_STORAGE_KEY);
    } catch {
      /* best-effort */
    }
  }

  // Breaks for the current session — exams.js config, unless a dev/test
  // override (?breakAfter=…) is active on a non-production host.
  function getSessionBreaks() {
    if (!session) return [];
    // Session-relative breaks (case sims, Phase 2) take precedence over the
    // static per-cert config; the QA override still wins on non-prod hosts.
    return getTestBreakOverride() ?? session.breaks ?? getExamBreaks(session.examId);
  }

  // Returns the configured break to offer when the learner moves forward off
  // `fromIndex`, or null. Triggers only on the FIRST forward processing of the
  // threshold question (Next or Skip from Q60 → Q61, etc.) — Previous/jump
  // navigation and revisit-pass traffic never re-trigger a break.
  function findPendingBreak(fromIndex) {
    if (!session || session.mode !== 'full' || timeExpired) return null;
    if (highWaterMark >= fromIndex) return null; // already processed earlier
    const breaks = getSessionBreaks();
    const idx = breaks.findIndex((b) => b.afterQuestion === fromIndex + 1);
    if (idx === -1) return null;
    if (breaksTaken.includes(breaks[idx].afterQuestion)) return null;
    return { ...breaks[idx], breakNumber: idx + 1, totalBreaks: breaks.length };
  }

  // Shows the break offer screen and seals the prior section. Locking happens
  // at the break point itself — taken OR skipped — mirroring the real PMI
  // exam, where a completed section cannot be reopened.
  //
  // Mutates `newSkipped` in place (the caller is about to commit it via
  // setSkippedIndices): locked questions can no longer be answered, so they
  // leave the revisit queue. everSkippedIndices is intentionally untouched —
  // the navigator keeps showing them as skipped, and they simply score as
  // unanswered (exactly what would happen at a real PMI workstation).
  function applyBreakLock(pendingBreak, newSkipped) {
    for (const idx of [...newSkipped]) {
      if (idx < pendingBreak.afterQuestion) newSkipped.delete(idx);
    }
    setLockedThreshold(pendingBreak.afterQuestion);
    // Mark this break — and any earlier one now inside the locked range — as
    // passed so none of them is offered again.
    setBreaksTaken(
      getSessionBreaks()
        .filter((b) => b.afterQuestion <= pendingBreak.afterQuestion)
        .map((b) => b.afterQuestion)
    );
    setBreakState({
      status: 'offered',
      breakNumber: pendingBreak.breakNumber,
      totalBreaks: pendingBreak.totalBreaks,
      afterQuestion: pendingBreak.afterQuestion,
      durationMinutes: pendingBreak.durationMinutes,
      startedAt: null,
    });
    trackEvent('exam_break_offered', 'usage', {
      exam_id: session.examId,
      break_after_question: pendingBreak.afterQuestion,
    });
  }

  function handleStartBreak() {
    setBreakState((bs) => (bs ? { ...bs, status: 'active', startedAt: Date.now() } : bs));
    trackEvent('exam_break_started', 'usage', { exam_id: session?.examId });
  }

  function handleSkipBreak() {
    trackEvent('exam_break_skipped', 'usage', { exam_id: session?.examId });
    setBreakState(null);
  }

  // Resume Exam click OR the 10-minute countdown reaching 00:00.
  function handleResumeFromBreak() {
    trackEvent('exam_break_resumed', 'usage', { exam_id: session?.examId });
    setBreakState(null);
  }

  function handleNext(bypassCheckpoint = false) {
    if (timeExpired) return;

    const total = session.questions.length;
    const forwardPassDone = highWaterMark >= total - 1;
    const wasRevisiting = forwardPassDone && skippedIndices.has(currentIndex);

    const newQStates = buildSavedStates();
    const newSkipped = new Set(skippedIndices);
    newSkipped.delete(currentIndex); // answered — no longer needs revisiting
    // Phase 3.6+ — clear the persistent skipped-display marker too once the
    // learner has actually answered the question.
    const newEverSkipped = new Set(everSkippedIndices);
    newEverSkipped.delete(currentIndex);

    const newHWM = Math.max(highWaterMark, currentIndex);
    // PMI-style break trigger — fires the first time the learner moves
    // forward past a configured break threshold (Simulation only). Locks the
    // prior section and shows the Break Screen over the next question.
    //
    // Checkpoint (Bryan, 2026-07-12): before the lock is applied, surface any
    // flagged or unanswered questions in the section — this is the learner's
    // LAST chance to touch them. The move is NOT committed (highWaterMark is
    // untouched), so the break stays armed and re-offers on the next forward
    // move off this question. `bypassCheckpoint === true` (strict — click
    // events must not match) is the "Continue to Break" path.
    const pendingBreak = findPendingBreak(currentIndex);
    if (pendingBreak && bypassCheckpoint !== true) {
      const unresolved = computeUnresolvedRange({
        rangeStart: lockedThreshold,
        rangeEnd: pendingBreak.afterQuestion,
        questionStates: newQStates,
        flaggedIndices,
      });
      if (unresolved.length > 0) {
        // Commit only the just-saved snapshot so nothing entered is lost.
        setQuestionStates(newQStates);
        setCheckpointState({ resume: 'next', afterQuestion: pendingBreak.afterQuestion, unresolved });
        trackEvent('exam_checkpoint_shown', 'usage', {
          exam_id: session.examId,
          unresolved_count: unresolved.length,
          break_after_question: pendingBreak.afterQuestion,
        });
        return;
      }
    }
    if (pendingBreak) applyBreakLock(pendingBreak, newSkipped);
    const nextIdx = getNextQuestion(currentIndex, total, newSkipped, wasRevisiting);
    const { selectedAnswers: sa, submitted: sub, eliminatedChoices: ec } = restoreState(newQStates, nextIdx);

    setCurrentIndex(nextIdx);
    setSkippedIndices(newSkipped);
    setEverSkippedIndices(newEverSkipped);
    setHighWaterMark(newHWM);
    setQuestionStates(newQStates);
    setSelectedAnswers(sa);
    setShowExplanation(sub);
    setEliminatedChoices(ec);
  }

  // Called by QuestionCard's "Skip →" button.
  //
  // Forward pass: marks the question as skipped (only when unanswered) and
  //               advances to the next sequential question.  If this is the
  //               last forward question and skipped questions remain, the
  //               revisit pass begins automatically.
  //
  // Revisit pass: dismisses the question from the revisit queue (removes from
  //               skippedIndices) and advances to the next skipped question.
  //               When none remain, the session completes.
  function handleSkip(bypassCheckpoint = false) {
    if (timeExpired || showExplanation) return;
    if (currentIndex < lockedThreshold) return; // locked section — read-only

    const total = session.questions.length;
    const forwardPassDone = highWaterMark >= total - 1;
    const wasRevisiting = forwardPassDone && skippedIndices.has(currentIndex);

    const newQStates = buildSavedStates();
    const newSkipped = new Set(skippedIndices);

    if (!wasRevisiting) {
      if (selectedAnswers.length === 0) newSkipped.add(currentIndex);
    } else {
      newSkipped.delete(currentIndex); // revisit-pass dismiss
    }

    // Phase 3.6+ — track every index that's ever been skipped (forward OR
    // revisit dismiss) and not yet answered.  This drives the navigator's
    // "skipped" indicator, which previously reset to "unseen" after a
    // revisit-pass dismissal.  Add unconditionally on any skip action where
    // the question wasn't already answered.
    const newEverSkipped = new Set(everSkippedIndices);
    if (newQStates[currentIndex]?.submitted !== true) {
      newEverSkipped.add(currentIndex);
    }

    const newHWM = Math.max(highWaterMark, currentIndex);
    // PMI-style break trigger — fires the first time the learner moves
    // forward past a configured break threshold (Simulation only). Locks the
    // prior section and shows the Break Screen over the next question.
    //
    // Checkpoint (Bryan, 2026-07-12): before the lock is applied, surface any
    // flagged or unanswered questions in the section — this is the learner's
    // LAST chance to touch them. The move is NOT committed (highWaterMark is
    // untouched), so the break stays armed and re-offers on the next forward
    // move off this question. `bypassCheckpoint === true` (strict — click
    // events must not match) is the "Continue to Break" path.
    const pendingBreak = findPendingBreak(currentIndex);
    if (pendingBreak && bypassCheckpoint !== true) {
      const unresolved = computeUnresolvedRange({
        rangeStart: lockedThreshold,
        rangeEnd: pendingBreak.afterQuestion,
        questionStates: newQStates,
        flaggedIndices,
      });
      if (unresolved.length > 0) {
        // Commit only the just-saved snapshot so nothing entered is lost.
        setQuestionStates(newQStates);
        setCheckpointState({ resume: 'skip', afterQuestion: pendingBreak.afterQuestion, unresolved });
        trackEvent('exam_checkpoint_shown', 'usage', {
          exam_id: session.examId,
          unresolved_count: unresolved.length,
          break_after_question: pendingBreak.afterQuestion,
        });
        return;
      }
    }
    if (pendingBreak) applyBreakLock(pendingBreak, newSkipped);
    const nextIdx = getNextQuestion(currentIndex, total, newSkipped, wasRevisiting);
    const { selectedAnswers: sa, submitted: sub, eliminatedChoices: ec } = restoreState(newQStates, nextIdx);

    setCurrentIndex(nextIdx);
    setSkippedIndices(newSkipped);
    setEverSkippedIndices(newEverSkipped);
    setHighWaterMark(newHWM);
    setQuestionStates(newQStates);
    setSelectedAnswers(sa);
    setShowExplanation(sub);
    setEliminatedChoices(ec);
  }

  // Called by QuestionCard's "← Previous" button.
  //
  // Saves the current question's snapshot and restores the one for
  // currentIndex - 1.  Does NOT touch highWaterMark or skippedIndices —
  // those are the responsibility of handleNext / handleSkip only.
  // The Previous button is disabled on the first question (currentIndex === 0).
  function handlePrevious() {
    if (currentIndex === 0) return; // safety guard; button is already disabled

    const prevIdx = currentIndex - 1;
    const newQStates = buildSavedStates();
    const { selectedAnswers: sa, submitted: sub, eliminatedChoices: ec } = restoreState(newQStates, prevIdx);

    setCurrentIndex(prevIdx);
    setQuestionStates(newQStates);
    setSelectedAnswers(sa);
    setShowExplanation(sub);
    setEliminatedChoices(ec);
    // highWaterMark and skippedIndices are intentionally left unchanged
  }

  // Toggles the review flag on a question by index.
  // Flagging is completely independent from selection / submission state.
  function handleToggleFlag(index) {
    if (index < lockedThreshold) return; // locked section — flags are sealed
    setFlaggedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }

  // Navigates directly to any question via the QuestionNavigator.
  //
  // Saves the current question's snapshot (preserving any in-progress
  // selection) and restores the target question's snapshot.
  //
  // Does NOT alter highWaterMark or skippedIndices — those are only
  // advanced by handleNext / handleSkip.  This allows learners to jump
  // freely without disrupting the forward-pass / revisit-pass logic.
  function handleJumpToQuestion(targetIndex) {
    if (targetIndex === currentIndex) return;
    if (!session) return;
    if (targetIndex < 0 || targetIndex >= session.questions.length) return;

    trackEvent('navigator_jump', 'usage', { exam_id: session.examId });

    const newQStates = buildSavedStates();
    const { selectedAnswers: sa, submitted: sub, eliminatedChoices: ec } =
      restoreState(newQStates, targetIndex);

    setQuestionStates(newQStates);
    setCurrentIndex(targetIndex);
    setSelectedAnswers(sa);
    setShowExplanation(sub);
    setEliminatedChoices(ec);
    // highWaterMark and skippedIndices intentionally unchanged
  }

  // Called when the user confirms "End Exam" in the confirmation modal.
  //
  // Saves the current question's snapshot so that any in-progress selection
  // is preserved in questionStates.  Then advances currentIndex past the end
  // of the question array, which triggers the completion screen on the next
  // render.  highWaterMark and skippedIndices are NOT altered — they reflect
  // the true progress made before the early finish.
  //
  // Unanswered and skipped questions are simply absent from (or have
  // submitted:false in) questionStates.  They are not auto-submitted and
  // therefore do not contribute to any score calculation.
  function handleFinishEarly() {
    const newQStates = buildSavedStates();
    setQuestionStates(newQStates);
    setCurrentIndex(session.questions.length); // trigger completion screen
    setEarlyFinish(true);
    setShowFinishConfirm(false);
    setShowExplanation(false);
  }

  // ── Abandoned sessions ────────────────────────────────────────────────────
  //
  // persistExamSession used to run from one place only: the effect above, gated
  // on `timeExpired || currentIndex >= session.questions.length`. Everything
  // else threw the session away. handleReset, handleBackToExamSelect and
  // handleBackToModeSelect each call setSession(null) and clear questionStates,
  // and there was no unload path at all — so a learner who answered and then
  // pressed "← Change Mode", or simply closed the tab, lost the lot: no
  // exam_sessions row, no question_attempts, nothing in their history.
  //
  // telemetryService flushes on `pagehide`, so the ANSWER EVENT survived a tab
  // close while the answer itself did not. That asymmetry is how 8 of 100
  // activated learners in prod showed as activated with zero rows.
  //
  // These sessions now persist with completion_reason 'abandoned'. Migration
  // 106 keeps them out of learner_session_summary, so the learner's own
  // history, readiness and streak are unchanged — the data exists for admin
  // surfaces and nothing is silently thrown away.
  const liveSessionRef = useRef(null);
  useEffect(() => {
    liveSessionRef.current = session
      ? { session, questionStates, skippedIndices, flaggedIndices }
      : null;
  }, [session, questionStates, skippedIndices, flaggedIndices]);

  /**
   * Persist an in-progress session that is about to be discarded.
   *
   * No-ops unless there is a live session that has not already been persisted
   * and the learner actually answered something — an untouched session carries
   * nothing worth keeping, and writing one would turn "opened the deck and
   * left" into a row that looks like practice.
   *
   * Best-effort by design: fire-and-forget so no exit path can ever be blocked
   * or slowed by a write, exactly as the completion path does.
   */
  const persistAbandonedSession = useCallback(() => {
    const live = liveSessionRef.current;
    if (!shouldPersistAbandonedSession(live, sessionPersistedRef.current)) return;

    sessionPersistedRef.current = true;
    try {
      Promise.resolve(
        persistExamSession(
          live.session, live.questionStates, live.skippedIndices,
          'abandoned', live.flaggedIndices, { timeUsedSeconds: null },
        ),
      ).catch(() => { /* never surface a write failure on the way out */ });
    } catch (_) { /* never block an exit path */ }
  }, []);

  // Tab close / app backgrounding. Best-effort only: the browser may kill the
  // request before it lands, and an authenticated Supabase write cannot go
  // through sendBeacon (it sets no headers). The in-app exits below are the
  // reliable path; this catches what it can.
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') persistAbandonedSession();
    };
    window.addEventListener('pagehide', persistAbandonedSession);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      window.removeEventListener('pagehide', persistAbandonedSession);
      document.removeEventListener('visibilitychange', onHide);
    };
  }, [persistAbandonedSession]);

  function handleReset() {
    persistAbandonedSession();
    selectedExamRef.current = null;
    setExamId(null);
    setSession(null);
    setPendingTargetedTest(false);
    setCurrentIndex(0);
    setSkippedIndices(new Set());
    setEverSkippedIndices(new Set());
    setHighWaterMark(-1);
    setQuestionStates({});
    setSelectedAnswers([]);
    setEliminatedChoices([]);
    setShowExplanation(false);
    setTimeExpired(false);
    setShowFinishConfirm(false);
    setEarlyFinish(false);
    setFlaggedIndices(new Set());
    resetBreakState();
    setNavVisible(true);
  }

  function handleBackToExamSelect() {
    persistAbandonedSession();
    // Return the address bar to the clean home URL. replaceState (not
    // history.back()) so a deep-linked visitor with no prior entry stays in
    // the app; the extra inert history entry this leaves is harmless.
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get('view') === 'session-setup') {
        url.searchParams.delete('view');
        url.searchParams.delete('cert');
        window.history.replaceState({ pmPixelView: 'home' }, '', url.toString());
      }
    } catch (_) { /* History API unavailable */ }
    selectedExamRef.current = null;
    setExamId(null);
    setSession(null);
    setPendingTargetedTest(false);
    setSkippedIndices(new Set());
    setEverSkippedIndices(new Set());
    setHighWaterMark(-1);
    setQuestionStates({});
    setEliminatedChoices([]);
    setTimeExpired(false);
    setShowFinishConfirm(false);
    setEarlyFinish(false);
    setFlaggedIndices(new Set());
    resetBreakState();
  }

  function handleBackToModeSelect() {
    persistAbandonedSession();
    setSession(null);
    setPendingTargetedTest(false);
    setCurrentIndex(0);
    setSkippedIndices(new Set());
    setEverSkippedIndices(new Set());
    setHighWaterMark(-1);
    setQuestionStates({});
    setSelectedAnswers([]);
    setEliminatedChoices([]);
    setShowExplanation(false);
    setTimeExpired(false);
    setShowFinishConfirm(false);
    setEarlyFinish(false);
    setFlaggedIndices(new Set());
    resetBreakState();
    setNavVisible(true);
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  // ── Admin overlay — checked FIRST so it can appear over any screen ────────
  //
  // All learner state (examId, session, currentIndex, …) remains untouched
  // while the overlay is mounted.  Closing the overlay simply sets
  // showAdmin=false and the normal routing falls through to wherever the
  // learner was.
  // ── Phase 3.6 — deep-link preview (full-screen) ─────────────────────────
  if (previewParamsActive) {
    return (
      <div data-testid="app-preview-screen" style={{ background: '#f5f5f5', minHeight: '100vh' }}>
        {previewLoading && (
          <div style={{ padding: '48px 24px', textAlign: 'center', color: '#888' }}>
            Loading preview…
          </div>
        )}
        {previewLoadError && (
          <div data-testid="app-preview-error" style={{ padding: '48px 24px', textAlign: 'center' }}>
            <div style={{ color: '#c62828', fontWeight: 600, marginBottom: '12px' }}>
              {previewLoadError}
            </div>
            <button
              onClick={closePreviewDeepLink}
              style={{ padding: '8px 16px', cursor: 'pointer' }}
            >
              ← Return to app
            </button>
          </div>
        )}
        {!previewLoading && !previewLoadError && previewQuestion && (
          <QuestionPreviewModal
            question={previewQuestion}
            onClose={closePreviewDeepLink}
            variant="page"
          />
        )}
      </div>
    );
  }

  // ── Admin overlay takes precedence over learner view routing ────────────
  // The admin overlay is controlled by `showAdmin`/`adminView`, independent of
  // the learner `view` state, so it MUST be checked before the view-based
  // learner routes below. Otherwise a leftover `view === 'dashboard'` (Learner
  // Dashboard) — including one restored by a browser Back / popstate — would
  // render on top of the admin screens. The popstate handler routes Back
  // presses through the admin stack while the overlay is open.
  if (showAdmin) {
    return (
      <AdminGate onBack={handleAdminBack}>
        {renderAdminContent()}
        {/* Mission Control (adminView=null) renders its own copy inside
            AdminScreen; mounting here only for sub-views keeps the
            admin-open-* testids unique. */}
        {adminView && (
          <AdminQuickActions
            activeView={adminView}
            reportCount={reports.length}
            onAction={handleQuickAction}
          />
        )}
      </AdminGate>
    );
  }

  // ── Phase E2 — view-based routing for learner pages ─────────────────────
  // These render INSTEAD of the exam flow when the learner navigates via
  // the header user menu. The navigation callback handed to each page is a
  // direct setView — pages stay focused on UI, not on event plumbing.
  if (view === 'dashboard') {
    return (
      <LearnerDashboard
        onNavigate={setView}
        onStartSession={handleStartPractice}
        onStartCert={handleStartCert}
      />
    );
  }
  if (view === 'history') {
    return (
      <PerformanceHistory
        embedded
        onNavigate={setView}
        onStartFirstSession={handleStartPractice}
        onReviewSession={(sessionId) => openSessionReview(sessionId, 'history')}
        initialExamId={historyExamId}
        onExamChange={setHistoryExamId}
      />
    );
  }
  // Reopenable review of one completed attempt. Owner-only (the loader RPC
  // enforces it); a signed-out visitor gets the sign-in prompt and returns to
  // this exact session afterwards.
  if (view === 'results') {
    return (
      <SessionResultsView
        sessionId={reviewSessionId}
        source={reviewSource}
        onNavigate={setView}
        onSignIn={() => {
          try {
            window.dispatchEvent(new CustomEvent('pm-pixel:auth', { detail: { mode: 'signin' } }));
          } catch (_) { /* CustomEvent unavailable */ }
        }}
      />
    );
  }
  if (view === 'plan') {
    // examId on the profile is the active cert; we surface whichever cert
    // has data. The page itself handles "no plan" empty states.
    //
    // "Set your exam date" / "Edit target date" open the StudyPlanEditor modal
    // (previously these dead-ended back to the dashboard / baseline screen).
    return (
      <>
        <StudyPlanView
          key={`${planActiveCert}-${planRefreshKey}`}
          embedded
          activeCert={planActiveCert}
          onCertChange={setPlanActiveCert}
          onStartCert={handleStartCert}
          onNavigate={setView}
          onEditPlan={() => setPlanEditorOpen(true)}
          onStartActivity={handleStartPractice}
        />
        <StudyPlanEditor
          isOpen={planEditorOpen}
          onClose={() => setPlanEditorOpen(false)}
          onSaved={() => {
            setPlanEditorOpen(false);
            setPlanRefreshKey((k) => k + 1); // remount StudyPlanView so it reloads the new plan
          }}
          activeCert={planActiveCert}
          readiness={null}
        />
      </>
    );
  }
  if (view === 'readiness') {
    return (
      <ReadinessPage
        onNavigate={setView}
        onStartCert={handleStartCert}
        onStartSession={(certId) => (certId ? handleStartCert(certId) : handleStartPractice())}
      />
    );
  }
  if (view === 'milestones') {
    return <MilestonesPage onNavigate={setView} onStartCert={handleStartCert} />;
  }
  // Public Founding Tester / Beta landing page. Deep-linkable via
  // ?view=founding-tester and requires NO authentication — it renders for
  // anonymous visitors arriving from Reddit, LinkedIn, study groups, etc.
  if (view === 'founding-tester') {
    return <FoundingTesterLanding />;
  }

  // Public Founder Wall — Community → Founding Testers. Deep-linkable via
  // ?view=founding-testers; renders for anonymous visitors and signed-in
  // learners alike (the curated Hall of Fame from migration 050).
  if (view === 'founding-testers') {
    return <FoundersWallPage />;
  }

  // ── Admin content renderer (called inside AdminGate) ──────────────────
  function renderAdminContent() {
    if (adminView === 'dashboard') {
      return (
        <AdminDashboard
          onBack={() => setAdminView(null)}
          onEditQuestion={handleEditDashboardQuestion}
          initialExam={adminExamId || 'all'}
        />
      );
    }
    if (adminView === 'editor') {
      return (
        <QuestionEditor
          onBack={handleEditorBack}
          initialExamId={adminExamId}
          initialQuestionId={adminQuestionId}
          initialFilterUnreviewed={adminFilterUnreviewed}
          initialBatchIds={adminBatchIds}
          questionStores={questionStores}
          onQuestionStoresChange={setQuestionStores}
        />
      );
    }
    if (adminView === 'bulk-import') {
      return (
        <BulkImport
          onBack={() => setAdminView(null)}
          questionStores={questionStores}
          onImport={handleImport}
          onReviewImported={(importedExamId, importedBatchIds = []) => {
            // Jump into the editor focused on the just-imported exam, with
            // the Unreviewed filter pre-enabled AND the list scoped to just
            // the batches that were saved. Together these let the reviewer
            // land directly on the items that still need human sign-off
            // instead of the entire unreviewed backlog.
            setAdminExamId(importedExamId);
            setAdminQuestionId(null);
            setAdminSource(null);
            setAdminFilterUnreviewed(true);
            setAdminBatchIds(importedBatchIds);
            setAdminView('editor');
          }}
        />
      );
    }
    if (adminView === 'reported-questions') {
      return (
        <ReportedQuestionsView
          reports={reports}
          onUpdateReport={handleUpdateReport}
          onRemoveReport={handleRemoveReport}
          onEditQuestion={handleEditReportedQuestion}
          onBack={() => setAdminView(null)}
          questionStores={questionStores}
          onUpdateLifecycle={handleUpdateQuestionLifecycle}
          initialTab={adminReportedView === 'bank-reported' ? 'question-bank' : 'reports'}
          initialQbExam={adminReportedCert}
          initialQbStatus={adminReportedView === 'bank-reported' ? 'all' : 'new'}
          initialQbReviewStatus={adminReportedView === 'bank-reported' ? 'reported' : 'all'}
        />
      );
    }
    if (adminView === 'data-tools') {
      return (
        <DataToolsPanel
          onBack={() => setAdminView(null)}
          onImport={handleImport}
        />
      );
    }
    if (adminView === 'question-performance') {
      return (
        <QuestionPerformance
          onBack={() => setAdminView(null)}
          questionStores={questionStores}
        />
      );
    }
    if (adminView === 'telemetry') {
      return (
        <TelemetryDashboard
          onBack={() => setAdminView(null)}
        />
      );
    }
    if (adminView === 'email-analytics') {
      return (
        <EmailAnalyticsScreen
          onBack={() => setAdminView(null)}
        />
      );
    }
    if (adminView === 'journey-activation') {
      return (
        <JourneyActivationScreen
          onBack={() => setAdminView(null)}
        />
      );
    }
    if (adminView === 'admin-users') {
      return (
        <AdminUsersScreen
          onBack={() => setAdminView(null)}
        />
      );
    }
    if (adminView === 'registered-users') {
      return (
        <RegisteredUsersScreen
          onBack={() => setAdminView(null)}
        />
      );
    }
    if (adminView === 'seo-health') {
      return <SeoHealthScreen onBack={() => setAdminView(null)} />;
    }
    if (adminView === 'release-notes') {
      return (
        <ReleaseNotesView
          onBack={() => setAdminView(null)}
        />
      );
    }
    if (adminView === 'badge-manager') {
      return (
        <BadgeManager
          onBack={() => setAdminView(null)}
        />
      );
    }
    if (adminView === 'question-factory') {
      return (
        <QuestionFactoryScreen
          onBack={() => setAdminView(null)}
          onOpenInEditor={handleOpenInEditorFromFactory}
          onQuestionStoreInvalidated={handleQuestionStoreInvalidated}
        />
      );
    }
    if (adminView === 'feedback') {
      return (
        <FeedbackHubView
          onBack={() => setAdminView(null)}
        />
      );
    }
    if (adminView === 'case-studies' && isFeatureEnabled('caseStudyAdminEnabled')) {
      return (
        <CaseStudiesScreen
          onBack={() => { setAdminCaseId(null); setAdminView(null); }}
          initialOpenId={adminCaseId}
          onEditQuestion={(questionId, certId, caseStudyId) => {
            setAdminExamId(certId);
            setAdminQuestionId(questionId);
            setAdminCaseId(caseStudyId);
            setAdminSource('case-studies');
            setAdminView('editor');
          }}
        />
      );
    }
    if (adminView === 'founding-testers-admin') {
      return (
        <FoundingTesterAdmin
          onBack={() => setAdminView(null)}
        />
      );
    }
    if (adminView === 'testimonials') {
      return (
        <TestimonialsManager
          onBack={() => setAdminView(null)}
        />
      );
    }
    return (
      <AdminScreen
        onBack={handleAdminBack}
        onOpenDashboard={(p) => { setAdminExamId(p && p.selectedCert ? p.selectedCert : null); setAdminView('dashboard'); }}
        onOpenEditor={(p) => { setAdminExamId(p && p.selectedCert ? p.selectedCert : null); setAdminQuestionId(null); setAdminFilterUnreviewed(false); setAdminBatchIds([]); setAdminView('editor'); }}
        onOpenBulkImport={() => setAdminView('bulk-import')}
        onOpenCaseStudies={() => setAdminView('case-studies')}
        onOpenReportedQuestions={(p) => { setAdminReportedCert(p && p.selectedCert ? p.selectedCert : 'all'); setAdminReportedView(p && p.view ? p.view : null); setAdminView('reported-questions'); }}
        onOpenQuestionPerformance={() => setAdminView('question-performance')}
        onOpenTelemetry={() => setAdminView('telemetry')}
        onOpenEmailAnalytics={() => setAdminView('email-analytics')}
        onOpenJourneyActivation={() => setAdminView('journey-activation')}
        onOpenDataTools={() => setAdminView('data-tools')}
        onOpenAdminUsers={() => setAdminView('admin-users')}
        onOpenRegisteredUsers={() => setAdminView('registered-users')}
        onOpenBadgeManager={() => setAdminView('badge-manager')}
        onOpenQuestionFactory={() => setAdminView('question-factory')}
        onOpenSeoHealth={() => setAdminView('seo-health')}
        onOpenReleaseNotes={() => setAdminView('release-notes')}
        onOpenFeedbackHub={() => setAdminView('feedback')}
        onOpenFoundingTesters={() => setAdminView('founding-testers-admin')}
        onOpenTestimonials={() => setAdminView('testimonials')}
        reportCount={reports.length}
      />
    );
  }

  if (!examId) {
    return (
      <div style={{ minHeight: '100vh', background: '#e8e5de' }}>
        <ExamSelector
          onSelectExam={handleSelectExam}
          onOpenFoundersWall={() => setView('founding-testers')}
          journeySlot={(
            <JourneyOnboarding
              onStartMicro={handleStartJourneyMicro}
              onStartBaseline={handleStartCert}
              onStartMode={handleStartJourneyMode}
              onNavigate={setView}
            />
          )}
        />
        <div style={{ textAlign: 'center', paddingBottom: '32px' }}>
          {/* Learner-facing admin entry moved to HeaderUserMenu (gated to the
              admin allow-list). This footer button is kept ONLY for test/E2E
              builds - smoke.spec.js and several jest suites drive admin flows
              through it, and CI E2E builds set REACT_APP_E2E_BYPASS_AUTH.
              Real users never see it; Ctrl+backtick remains the signed-out
              emergency entry for bootstrap admins. */}
          {SHOW_TEST_ADMIN_BUTTON && (
            <button
              data-testid="open-admin-button"
              style={{
                padding: '8px 20px',
                background: 'transparent',
                border: '2px solid #94a3b8',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: '600',
                color: '#64748b',
                transition: 'color 0.15s, border-color 0.15s',
              }}
              onClick={() => { setAdminSource('landing'); setShowAdmin(true); }}
            >
              ⚙️ Admin
            </button>
          )}
          <div style={{ marginTop: '14px', fontSize: '0.8rem', color: '#64748b' }}>
            <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#64748b' }}>Privacy Policy</a>
            <span style={{ margin: '0 8px' }}>·</span>
            <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#64748b' }}>Terms of Service</a>
            <div style={{ marginTop: '4px', opacity: 0.8 }}>© {new Date().getFullYear()} Campbellclan7 Consulting Inc. / PM Pixel</div>
          </div>
        </div>
      </div>
    );
  }

  if (!session) {
    // Targeted Test takes a detour through the configuration screen before
    // the session is built.
    if (pendingTargetedTest) {
      const tQuarantinedIds = new Set(
        reports.filter((r) => r.status === 'new').map((r) => r.questionId)
      );
      const rawPool = questionStores[examId] !== null
        ? questionStores[examId]
        : (targetedConfigPool || []);
      // Apply the same active-only + quarantine filter the session builder
      // would apply, so the metadata options and match counts the user sees
      // match the actual session pool.
      const liveTargetedPool = (rawPool || [])
        .filter((q) => !q.status || q.status === 'active')
        .filter((q) => !tQuarantinedIds.has(String(q.id)));
      return (
        <div style={styles.app}>
          <TargetedTestConfig
            examId={examId}
            pool={liveTargetedPool}
            onStart={handleStartTargetedTest}
            onBack={() => setPendingTargetedTest(false)}
            sessionStart={sessionStartStatus}
            onRetrySessionStart={handleRetrySessionStart}
          />
        </div>
      );
    }

    if (showCaseStudies) {
      return (
        <div style={styles.app}>
          <CaseStudyPracticeScreen
            examId={examId}
            user={learnerUser || null}
            onBack={() => {
              // Realign the address bar with the mode picker we land on.
              // replaceState (not history.back()) so a deep-linked visitor
              // with no prior history entry stays in the app.
              try {
                const url = new URL(window.location.href);
                if (url.searchParams.get('view') === 'case-study') {
                  url.searchParams.set('view', 'session-setup');
                  window.history.replaceState({ pmPixelView: 'home', pmPixelExam: examId }, '', url.toString());
                }
              } catch (_) { /* History API unavailable */ }
              setShowCaseStudies(false);
            }}
          />
        </div>
      );
    }

    return (
      <div style={styles.app}>
        <ModeSelector
          examId={examId}
          onSelectMode={handleSelectMode}
          onBack={handleBackToExamSelect}
          sessionStart={sessionStartStatus}
          onRetrySessionStart={handleRetrySessionStart}
        />
      </div>
    );
  }

  // Steps 3a/3b: Session ended — show Results page.
  //
  // Three paths lead here:
  //   timeExpired                             → completionReason = 'expired'
  //   currentIndex >= session.questions.length AND earlyFinish → 'early'
  //   currentIndex >= session.questions.length AND !earlyFinish → 'completed'
  //
  // questionStates and skippedIndices are passed as-is so ResultsPage can
  // compute per-question scores from the same snapshot that was saved when
  // the session ended.
  const sessionEnded = timeExpired || currentIndex >= session.questions.length;
  if (sessionEnded) {
    const completionReason = timeExpired ? 'expired' : earlyFinish ? 'early' : 'completed';
    return (
      <>
        <ResultsPage
          session={session}
          questionStates={questionStates}
          skippedIndices={skippedIndices}
          completionReason={completionReason}
          onReset={handleReset}
          onRetake={handleBackToModeSelect}
          onAnotherPractice={() => handleSelectMode('practice', session.examId)}
          onReport={(question, itemSelectedAnswers) =>
            handleOpenReport(question, session.examId, itemSelectedAnswers)
          }
          onStartTargetedQuiz={handleStartTargetedQuiz}
          flaggedIndices={flaggedIndices}
          examElapsedSeconds={paceElapsedSeconds}
          sessionStart={sessionStartStatus}
          onRetrySessionStart={handleRetrySessionStart}
          timeUsedSeconds={completedTimeUsedSeconds}
          sessionId={completedSessionId}
        />
        {reportModalState && (
          <ReportModal
            question={reportModalState.question}
            examId={reportModalState.examId}
            selectedAnswers={reportModalState.selectedAnswers}
            onSubmit={handleSubmitReport}
            onClose={handleCloseReportModal}
          />
        )}
      </>
    );
  }

  // Step 3c: Active question
  const currentQuestion = session.questions[currentIndex];
  const isRevisiting = skippedIndices.has(currentIndex);
  // Non-prod test override status for the header chip (null on prod hosts).
  const activeBreakOverride = session.mode === 'full' ? getTestBreakOverride() : null;

  return (
    <div style={styles.app} className={isMobile ? css.app : undefined}>
      {/*
        Header — four columns on desktop, collapses on tablet/mobile via CSS Module.
      */}
      <div style={!isTablet ? styles.header : undefined} className={css.header}>
        <div style={styles.headerLeft} className={css.headerLeft}>
          {/* Brand wordmark — top-left exam chrome. Sizing/path/styling live in
              PMPixelLogo + its CSS Module + the theme asset folder, so this stays
              a single self-contained tag (no hardcoded image path or styles). */}
          <PMPixelLogo size="medium" className={css.headerLogo} />
          <span style={styles.examBadge}>{session.displayName}</span>
          <span style={styles.modeBadge}>{session.modeName}</span>
          {activeBreakOverride && (
            <span
              data-testid="break-override-chip"
              style={styles.breakOverrideChip}
              title="Break test override armed — clear with ?breakAfter=off"
            >
              ⚙ test breaks @ Q{activeBreakOverride.map((b) => b.afterQuestion).join(', Q')}
            </span>
          )}
        </div>

        <div style={styles.progressCell} className={css.progressCell}>
          <span data-testid="question-progress" style={styles.progress}>
            Question {currentIndex + 1} of {session.totalQuestions}
          </span>
          {currentQuestion.referenceId && (
            <span
              data-testid="active-question-reference-id"
              style={styles.activeRefId}
            >
              {currentQuestion.referenceId}
            </span>
          )}
        </div>

        <div className={css.timerCell} data-pace-expanded={paceExpanded ? 'true' : 'false'}>
          <div className={css.timerBar}>
            <SessionTimer
              key={session.startedAt}
              durationMinutes={session.durationMinutes}
              onExpire={handleTimeExpired}
              paused={breakState !== null}
              onTick={handleTimerTick}
            />
            {paceEnabled && paceSnapshot && (
              <>
                <span className={css.barChip}>
                  <PaceChip snapshot={paceSnapshot} />
                </span>
                <button
                  type="button"
                  className={css.paceToggle}
                  onClick={() => setPaceExpanded((v) => !v)}
                  aria-expanded={paceExpanded}
                  aria-label={paceExpanded ? 'Hide pace details' : 'Show pace details'}
                >
                  <span className={`${css.chev} ${paceExpanded ? css.chevUp : ''}`} aria-hidden="true">▾</span>
                </button>
              </>
            )}
          </div>
          {paceEnabled && paceSnapshot && (
            <div className={`${css.paceDetail} ${paceExpanded ? css.paceDetailOpen : ''}`}>
              <PaceCard snapshot={paceSnapshot} />
            </div>
          )}
        </div>

        <div style={styles.headerActions} className={css.headerActions}>
          <button
            data-testid="finish-exam-button"
            style={styles.finishButton}
            onClick={() => setShowFinishConfirm(true)}
          >
            {isMobile ? 'Finish' : 'Finish Exam Now'}
          </button>
          <button
            data-testid="change-mode-button"
            style={styles.resetButton}
            onClick={handleBackToModeSelect}
          >
            ← {isMobile ? 'Mode' : 'Change Mode'}
          </button>
        </div>
      </div>

      {paceEnabled && (
        <PaceCoachStrip message={paceMessage} onDismiss={dismissPaceMessage} />
      )}

      {/* ── Two-column content: question (left) + navigator (right) ────── */}
      <div style={!isTablet ? styles.contentArea : undefined} className={css.contentArea}>

        {/* Question column */}
        <div style={styles.questionColumn} className={css.questionColumn}>

          {/* Case-block chrome (Phase 2): interstitial / scenario panel /
              case-complete banner. Renders nothing on standard sessions. */}
          <CaseExamPanel
            session={session}
            currentIndex={currentIndex}
            currentQuestion={currentQuestion}
          >

          {/* Revisit banner — only when the current question is being revisited */}
          {isRevisiting && (
            <div data-testid="revisit-banner" style={styles.revisitBanner}>
              ↺ Revisiting {skippedIndices.size} skipped question
              {skippedIndices.size !== 1 ? 's' : ''}
            </div>
          )}

          <QuestionCard
            question={currentQuestion}
            selectedAnswers={selectedAnswers}
            onSelectAnswer={setSelectedAnswers}
            onSubmit={handleSubmit}
            onPrevious={handlePrevious}
            isPreviousDisabled={currentIndex === 0}
            onSkip={handleSkip}
            isSkipped={isRevisiting}
            isSubmitted={showExplanation}
            eliminatedChoices={eliminatedChoices}
            onToggleElimination={handleToggleElimination}
            onReport={() => handleOpenReport(currentQuestion, session.examId, selectedAnswers)}
            isFlagged={flaggedIndices.has(currentIndex)}
            onFlag={() => handleToggleFlag(currentIndex)}
            mode={session.mode}
            onNext={handleNext}
            isLocked={currentIndex < lockedThreshold}
          />
          </CaseExamPanel>

          {/* ExplanationPanel is practice-style only (practice + Targeted
              Test). In full/simulation mode the QuestionCard button itself
              morphs to "Next Question →" — no separate panel is shown. */}
          {showExplanation && (session.mode === 'practice' || session.mode === 'targetedTest') && (
            <ExplanationPanel
              question={currentQuestion}
              selectedAnswers={selectedAnswers}
              onNext={handleNext}
            />
          )}

        </div>{/* /questionColumn */}

        {/* Navigator — desktop: sticky sidebar; tablet/mobile: drawer */}
        {!showDrawerNav && (
          <div style={styles.navigatorColumn} className={css.navigatorColumn}>
            <QuestionNavigator
              totalQuestions={session.questions.length}
              currentIndex={currentIndex}
              questionStates={questionStates}
              skippedIndices={skippedIndices}
              everSkippedIndices={everSkippedIndices}
              flaggedIndices={flaggedIndices}
              questions={session.questions}
              revealCorrectness={session.modeName !== 'Full Exam'}
              onJumpTo={handleJumpToQuestion}
              isVisible={navVisible}
              onToggle={() => setNavVisible((v) => !v)}
              lockedThreshold={lockedThreshold}
            />
          </div>
        )}

        {showDrawerNav && (
          <QuestionNavigator
            totalQuestions={session.questions.length}
            currentIndex={currentIndex}
            questionStates={questionStates}
            skippedIndices={skippedIndices}
            everSkippedIndices={everSkippedIndices}
            flaggedIndices={flaggedIndices}
            questions={session.questions}
            revealCorrectness={session.modeName !== 'Full Exam'}
            onJumpTo={handleJumpToQuestion}
            isVisible={navVisible}
            onToggle={() => setNavVisible((v) => !v)}
            lockedThreshold={lockedThreshold}
            isDrawer
          />
        )}

      </div>{/* /contentArea */}

      {/* ── Report Question modal ──────────────────────────────────────────── */}
      {reportModalState && (
        <ReportModal
          question={reportModalState.question}
          examId={reportModalState.examId}
          selectedAnswers={reportModalState.selectedAnswers}
          onSubmit={handleSubmitReport}
          onClose={handleCloseReportModal}
        />
      )}

      {/* ── Finish Exam Now confirmation modal ───────────────────────────────
          Checkpoint-enriched (Bryan, 2026-07-12): flagged / skipped / seen-but-
          unanswered questions in the unlocked range render as jump chips so the
          learner can resolve them — or end knowing exactly what's outstanding.
          Unseen questions collapse to a count (no 160-chip walls); questions
          sealed behind a break lock can't be changed, so they're a count too. */}
      {showFinishConfirm && session && (() => {
        const total = session.questions.length;
        const chips = [];
        let unseenCount = 0;
        let sealedCount = 0;
        for (let i = 0; i < total; i++) {
          // questionStates lags the live question — use showExplanation there.
          const answered = i === currentIndex
            ? showExplanation
            : questionStates[i]?.submitted === true;
          const flagged = flaggedIndices.has(i);
          if (answered && !flagged) continue;
          if (i < lockedThreshold) { sealedCount += 1; continue; }
          const seen = i <= highWaterMark || i === currentIndex
            || questionStates[i] != null || everSkippedIndices.has(i);
          if (!seen && !flagged) { unseenCount += 1; continue; }
          chips.push({ index: i, flagged, unanswered: !answered });
        }
        return (
          <div data-testid="finish-confirm-dialog" style={styles.confirmOverlay}>
            <div style={styles.confirmModal}>
              <h3 style={styles.confirmHeading}>End Exam Early?</h3>
              {chips.length > 0 ? (
                <>
                  <p style={styles.confirmBody}>
                    These questions are still flagged or unanswered — click one
                    to go straight to it, or end now and they will score as
                    incomplete.
                  </p>
                  <CheckpointChips
                    unresolved={chips}
                    onJumpTo={(i) => {
                      setShowFinishConfirm(false);
                      handleJumpToQuestion(i);
                    }}
                  />
                </>
              ) : (
                <p style={styles.confirmBody}>
                  Your progress will be saved. Unanswered and skipped questions
                  will be marked incomplete and will not count toward your score.
                </p>
              )}
              {unseenCount > 0 && (
                <p style={styles.confirmBody} data-testid="finish-unseen-count">
                  + {unseenCount} more question{unseenCount !== 1 ? 's' : ''} not
                  yet reached — also scored as incomplete.
                </p>
              )}
              {sealedCount > 0 && (
                <p style={styles.confirmBody} data-testid="finish-sealed-count">
                  {sealedCount} in the locked section can no longer be changed.
                </p>
              )}
              <div style={styles.confirmActions}>
                <button
                  style={styles.confirmCancelButton}
                  onClick={() => setShowFinishConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  style={styles.confirmEndButton}
                  onClick={handleFinishEarly}
                >
                  End Exam
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Pre-break unresolved-questions checkpoint ──────────────────────
          Shown INSTEAD of the break offer when the about-to-lock section still
          has flagged or unanswered questions. Chips jump straight to the
          question; Continue proceeds with the original (still-armed) move. */}
      {checkpointState && (
        <SectionCheckpoint
          afterQuestion={checkpointState.afterQuestion}
          unresolved={checkpointState.unresolved}
          onJumpTo={(i) => {
            trackEvent('exam_checkpoint_jump', 'usage', { exam_id: session?.examId });
            setCheckpointState(null);
            handleJumpToQuestion(i);
          }}
          onProceed={() => {
            const resume = checkpointState.resume;
            trackEvent('exam_checkpoint_proceeded', 'usage', {
              exam_id: session?.examId,
              unresolved_count: checkpointState.unresolved.length,
            });
            setCheckpointState(null);
            if (resume === 'skip') handleSkip(true); else handleNext(true);
          }}
          onDismiss={() => setCheckpointState(null)}
        />
      )}

      {/* ── PMI-style scheduled break overlay (Simulation only) ────────────
          Opaque full-screen overlay — renders over the next question so no
          content or answers are visible during the break. */}
      {breakState && (
        <ExamBreak
          phase={breakState.status}
          breakNumber={breakState.breakNumber}
          totalBreaks={breakState.totalBreaks}
          durationMinutes={breakState.durationMinutes}
          breakStartedAt={breakState.startedAt}
          onStart={handleStartBreak}
          onSkip={handleSkipBreak}
          onResume={handleResumeFromBreak}
          pacePanel={paceEnabled && paceSnapshot ? (
            <PaceBreakPanel
              snapshot={paceSnapshot}
              elapsedSeconds={paceElapsedSeconds}
              remainingSeconds={paceSecondsLeft ?? 0}
              personalizationTag={paceProfile.dominant}
            />
          ) : null}
        />
      )}
      <LegalFooter />
    </div>
  );
}
